/**
 * COACH DASHBOARD - 9-TILE MINIMALIST COACHING VIEW
 *
 * Answers in 10 seconds:
 * - Push / Maintain / Back off (today)
 * - Are fitness/strength/body comp trending right (weekly)
 * - Is nutrition helping/hurting
 */

import React, { useState, useEffect } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Spinner } from './ui';
import { Button } from '../design/primitives';
import { apiClient } from '../api/apiConfig';
import {
  HeartIcon,
  MoonIcon,
  FireIcon,
  BoltIcon,
  ClockIcon,
  ScaleIcon,
  ChartBarIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import MetricHistoryModal from './MetricHistoryModal';

// Coaching status type
interface CoachingStatus {
  status: 'not_started' | 'baseline_observation' | 'ready_for_tests' | 'active';
  fitness_goal: string | null;
  started_at: string | null;
  activated_at: string | null;
  baseline_progress?: {
    days_elapsed: number;
    min_days_required: number;
    ready_to_complete: boolean;
    completeness_pct: { [key: string]: number };
  };
  targets?: {
    steps_daily: number | null;
    strength_sessions_week: number;
    cardio_sessions_week: number;
    sleep_hours: number;
  };
}

interface ProgressionData {
  compliance: {
    [key: string]: {
      pct: number;
      days_hit?: number;
      days_tracked?: number;
    };
  };
  overall_compliance: number;
  can_progress: boolean;
  recommendations: Array<{
    metric: string;
    action: string;
    current: number;
    proposed: number;
    reason: string;
  }>;
}

interface PhysiologyData {
  loads: {
    acute_7d: number;
    chronic_28d_weekly: number;
    acwr: number | null;
  };
  readiness: {
    score: number;
    penalties: { [key: string]: number };
  };
  training_state: {
    state: 'push' | 'maintain' | 'deload' | 'blocked';
    flags: { [key: string]: boolean };
  };
  deload_until: string | null;
}

interface DashboardData {
  recovery: {
    score: number | null;
    status: 'green' | 'yellow' | 'red' | 'unknown';
    recommendation: string;
    date: string | null;  // Date the recovery data is from
    components: {
      hrv_score: number | null;
      rhr_score: number | null;
      sleep_score: number | null;
    };
    components_has_data?: {
      hrv: boolean;
      rhr: boolean;
      sleep: boolean;
    };
    reasons: string[];
    deltas: {
      hrv_pct: number | null;
      rhr_delta: number | null;
      sleep_debt_minutes: number | null;
    };
    penalty_breakdown?: Array<{
      component: string;
      delta: string;
      penalty_points: number;
      description: string;
    }>;
    total_penalty?: number;
    explanation?: string;
  };
  sleep: {
    avg_7d: number | null;
    bedtime_variance: number | null;
    sleep_debt: number | null;
  };
  nutrition: {
    today_score: number | null;
    goal_hit_today: boolean | null;
    hits_7d: number;
    tracked_days_7d: number;
    avg_score_7d: number | null;
    alcohol_days_7d: number;
    late_eat_days_7d: number;
  };
  cardio: {
    rhr_7d_avg: number | null;
    rhr_delta_30d: number | null;
    vo2_max_latest: number | null;
    vo2_max_delta_90d: number | null;
  };
  training_load: {
    total_minutes_week: number;
    hard_minutes_week: number;
    strength_sessions_week: number;
    delta_vs_last_week: number | null;
    workout_breakdown?: {
      [key: string]: {
        count: number;
        total_minutes: number;
      };
    };
    hr_zones?: {
      zone_0: number;
      zone_1: number;
      zone_2: number;
      zone_3: number;
      zone_4: number;
      zone_5: number;
    };
    total_trimp?: number;
  };
  strength: {
    top_lift_exercise: string | null;
    top_lift_e1rm: number | null;
    delta_4week_pct: number | null;
  };
  body_comp: {
    weight_7d_avg: number | null;
    weight_delta_30d: number | null;
    waist_latest: number | null;
    body_fat_trend: number | null;
    bmi: number | null;
  };
}

/** Raw API responses that can be preloaded by parent */
export interface CoachDashboardPreloadedData {
  recovery: any;
  dashboard: any;
  nutritionSnapshots: any[];
  coaching: any;
  weightGoals: any[];
  processGoals: any[];
}

interface CoachDashboardProps {
  onOpenWorkoutForm: () => void;
  onOpenStrengthForm: () => void;
  onOpenBodyCompForm: () => void;
  /** Pre-loaded data from parent - if provided, skip fetching */
  preloadedData?: CoachDashboardPreloadedData;
}

const CoachDashboard: React.FC<CoachDashboardProps> = ({
  onOpenWorkoutForm,
  onOpenStrengthForm,
  onOpenBodyCompForm,
  preloadedData
}) => {
  const [loading, setLoading] = useState(!preloadedData);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [aggregates, setAggregates] = useState<any[]>([]);
  const [userTargets, setUserTargets] = useState<any>(null);

  // Coaching state
  const [coachingStatus, setCoachingStatus] = useState<CoachingStatus | null>(null);
  const [progressionData, setProgressionData] = useState<ProgressionData | null>(null);
  const [physiologyData, setPhysiologyData] = useState<PhysiologyData | null>(null);
  const [quickNutrition, setQuickNutrition] = useState<{
    protein_hit: boolean | null;
    energy_intent_hit: boolean | null;
    no_alcohol: boolean | null;
    plants_hit: boolean | null;
  }>({ protein_hit: null, energy_intent_hit: null, no_alcohol: null, plants_hit: null });
  const [nutritionSaving, setNutritionSaving] = useState(false);
  const [nutritionDate, setNutritionDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Modal state for historical graphs
  const [sleepModalOpen, setSleepModalOpen] = useState(false);
  const [cardioRHRModalOpen, setCardioRHRModalOpen] = useState(false);
  const [cardioVO2ModalOpen, setCardioVO2ModalOpen] = useState(false);
  const [weightModalOpen, setWeightModalOpen] = useState(false);
  const [bodyFatModalOpen, setBodyFatModalOpen] = useState(false);
  const [hrvModalOpen, setHRVModalOpen] = useState(false);
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
  const [workoutModalOpen, setWorkoutModalOpen] = useState(false);
  const [stressModalOpen, setStressModalOpen] = useState(false);

  // Health goals
  const [weightGoal, setWeightGoal] = useState<{
    target_value: number;
    start_value: number | null;
    start_date: string;
    end_date: string;
  } | null>(null);

  // Process goals (for chart targets)
  const [processGoals, setProcessGoals] = useState<Record<string, number>>({});

  // Fetch nutrition answers when date changes
  const todayStr = new Date().toISOString().split('T')[0];
  useEffect(() => {
    const fetchNutritionForDate = async () => {
      try {
        const res = await apiClient.get('/api/nutrition-snapshots/', { params: { date: nutritionDate } });
        const snapshots = Array.isArray(res.data) ? res.data : (res.data.results || []);
        if (snapshots.length > 0) {
          const s = snapshots[0];
          setQuickNutrition({
            protein_hit: s.protein_hit ?? null,
            energy_intent_hit: s.energy_intent_hit ?? null,
            no_alcohol: s.no_alcohol ?? null,
            plants_hit: s.plants_hit ?? null,
          });
        } else {
          setQuickNutrition({ protein_hit: null, energy_intent_hit: null, no_alcohol: null, plants_hit: null });
        }
      } catch {
        setQuickNutrition({ protein_hit: null, energy_intent_hit: null, no_alcohol: null, plants_hit: null });
      }
    };
    fetchNutritionForDate();
  }, [nutritionDate]);

  useEffect(() => {
    if (preloadedData) {
      // Use preloaded data from parent
      processPreloadedData(preloadedData);
    } else {
      // Fetch data ourselves
      fetchDashboardData();
    }
  }, [preloadedData]);

  const processPreloadedData = async (pData: CoachDashboardPreloadedData) => {
    try {
      const { recovery, dashboard, nutritionSnapshots, coaching, weightGoals, processGoals: pGoals } = pData;

      // Weight goals
      if (weightGoals.length > 0) {
        const g = weightGoals[0];
        setWeightGoal({
          target_value: Number(g.target_value),
          start_value: g.start_value !== null ? Number(g.start_value) : null,
          start_date: g.start_date,
          end_date: g.end_date
        });
      }

      // Process goals -> target lookup map
      const goalsMap: Record<string, number> = {};
      pGoals.forEach((g: any) => {
        goalsMap[g.metric_type] = Number(g.target_value);
      });
      setProcessGoals(goalsMap);

      // Set coaching status
      if (coaching) {
        setCoachingStatus(coaching);

        // If coaching is active, fetch progression and physiology data (async, not blocking)
        if (coaching.status === 'active') {
          try {
            const [progressionRes, physiologyRes] = await Promise.all([
              apiClient.get('/api/coaching/progression/'),
              apiClient.get('/api/coaching/physiology/')
            ]);
            setProgressionData(progressionRes.data);
            setPhysiologyData(physiologyRes.data);
          } catch (e) {
            // Silently fail - not critical for initial render
          }
        }
      }

      // Fetch physiology data even if coaching not active (for monitoring)
      try {
        const physiologyRes = await apiClient.get('/api/coaching/physiology/');
        setPhysiologyData(physiologyRes.data);
      } catch (e) {
        // Silently fail
      }

      // Store aggregates for sparklines and user targets
      setAggregates(dashboard.aggregates || []);
      setUserTargets(dashboard.user_targets || null);

      // Process data using backend computed_metrics (single source of truth)
      const processedData = processDashboardData(recovery, dashboard, nutritionSnapshots);
      setData(processedData);
    } catch (err: any) {
      console.error('Error processing preloaded data:', err);
      setError(err.message || 'Failed to process dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Strategy: Make API calls in parallel
      // 1. Recovery Index
      // 2. Dashboard endpoint with computed_metrics (single source of truth)
      // 3. Nutrition summary (last 7 days)
      // 4. Coaching status
      // 5. Progression data (if coaching active)

      const [recoveryRes, dashboardRes, nutritionRes, coachingRes, weightGoalsRes, processGoalsRes] = await Promise.all([
        apiClient.get('/api/recovery-index/'),
        apiClient.get('/api/daily-health-aggregates/dashboard/?days=90'),
        apiClient.get('/api/nutrition-snapshots/?days=7'),
        apiClient.get('/api/coaching/status/').catch(() => ({ data: null })),
        apiClient.get('/api/health-goals/', { params: { metric_type: 'weight', status: 'active' } }).catch(() => ({ data: [] })),
        apiClient.get('/api/health-goals/', { params: { goal_type: 'process', status: 'active' } }).catch(() => ({ data: [] }))
      ]);

      const recovery = recoveryRes.data;
      const dashboardData = dashboardRes.data;
      const nutritionSnapshots = Array.isArray(nutritionRes.data) ? nutritionRes.data : (nutritionRes.data.results || []);
      const coaching = coachingRes.data;
      const weightGoals = weightGoalsRes.data.results || weightGoalsRes.data || [];
      if (weightGoals.length > 0) {
        const g = weightGoals[0];
        setWeightGoal({
          target_value: Number(g.target_value),
          start_value: g.start_value !== null ? Number(g.start_value) : null,
          start_date: g.start_date,
          end_date: g.end_date
        });
      }

      // Process goals -> target lookup map
      const processGoalsList = processGoalsRes.data.results || processGoalsRes.data || [];
      const goalsMap: Record<string, number> = {};
      processGoalsList.forEach((g: any) => {
        goalsMap[g.metric_type] = Number(g.target_value);
      });
      setProcessGoals(goalsMap);

      // Set coaching status
      if (coaching) {
        setCoachingStatus(coaching);

        // If coaching is active, fetch progression and physiology data
        if (coaching.status === 'active') {
          try {
            const [progressionRes, physiologyRes] = await Promise.all([
              apiClient.get('/api/coaching/progression/'),
              apiClient.get('/api/coaching/physiology/')
            ]);
            setProgressionData(progressionRes.data);
            setPhysiologyData(physiologyRes.data);
          } catch (e) {
          }
        }
      }

      // Fetch physiology data even if coaching not active (for monitoring)
      try {
        const physiologyRes = await apiClient.get('/api/coaching/physiology/');
        setPhysiologyData(physiologyRes.data);
      } catch (e) {
      }

      // Store aggregates for sparklines and user targets
      setAggregates(dashboardData.aggregates || []);
      setUserTargets(dashboardData.user_targets || null);

      // Process data using backend computed_metrics (single source of truth)
      const processedData = processDashboardData(recovery, dashboardData, nutritionSnapshots);
      setData(processedData);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const processDashboardData = (
    recovery: any,
    dashboardData: any,
    nutritionSnapshots: any[]
  ): DashboardData => {
    // Use backend computed_metrics as single source of truth
    const computed = dashboardData.computed_metrics || {};

    // Nutrition (still from separate endpoint)
    const todayNutrition = nutritionSnapshots[0] || {};
    const nutrition7d = nutritionSnapshots;
    const nutritionHits = nutrition7d.filter(n => n.nutrition_goal_hit).length;
    const nutritionAvg = avg(nutrition7d.map(n => n.nutrition_score_0_100).filter(v => v != null));
    const alcoholDays = nutrition7d.filter(n => n.alcohol_consumed).length;
    const lateEatDays = nutrition7d.filter(n => n.ate_late).length;

    return {
      recovery: {
        score: recovery.score,
        status: recovery.status,
        recommendation: recovery.recommendation,
        date: recovery.date || null,
        components: recovery.components || {},
        components_has_data: recovery.components_has_data || {},
        reasons: recovery.reasons || [],
        deltas: recovery.deltas || {},
        penalty_breakdown: recovery.penalty_breakdown || [],
        total_penalty: recovery.total_penalty,
        explanation: recovery.explanation
      },
      sleep: {
        avg_7d: computed.sleep?.avg_7d_hours || null,
        bedtime_variance: computed.sleep?.bedtime_variance_minutes || null,
        sleep_debt: computed.sleep?.sleep_debt_minutes || null
      },
      nutrition: {
        today_score: todayNutrition.nutrition_score_0_100 || null,
        goal_hit_today: todayNutrition.nutrition_goal_hit || null,
        hits_7d: nutritionHits,
        tracked_days_7d: nutrition7d.length,
        avg_score_7d: nutritionAvg,
        alcohol_days_7d: alcoholDays,
        late_eat_days_7d: lateEatDays
      },
      cardio: {
        rhr_7d_avg: computed.cardio?.rhr_7d_avg || null,
        rhr_delta_30d: computed.cardio?.rhr_delta_vs_30d || null,
        vo2_max_latest: computed.cardio?.vo2_max_latest || null,
        vo2_max_delta_90d: computed.cardio?.vo2_max_delta_pct_vs_90d || null
      },
      training_load: {
        total_minutes_week: computed.training?.total_minutes_week || 0,
        hard_minutes_week: computed.training?.hard_minutes_week || 0,
        strength_sessions_week: computed.training?.strength_sessions_week || 0,
        delta_vs_last_week: computed.training?.delta_pct_vs_last_week || null,
        workout_breakdown: computed.training?.workout_breakdown || {},
        hr_zones: computed.training?.hr_zones || { zone_0: 0, zone_1: 0, zone_2: 0, zone_3: 0, zone_4: 0, zone_5: 0 },
        total_trimp: computed.training?.total_trimp || 0
      },
      strength: computed.strength || {
        top_lift_exercise: null,
        top_lift_e1rm: null,
        delta_4week_pct: null
      },
      body_comp: {
        weight_7d_avg: computed.body_comp?.weight_7d_avg_kg || null,
        weight_delta_30d: computed.body_comp?.weight_delta_pct_vs_30d || null,
        waist_latest: computed.body_comp?.waist_latest_cm || null,
        body_fat_trend: computed.body_comp?.body_fat_latest_pct || null,
        bmi: computed.body_comp?.bmi || null
      }
    };
  };

  const avg = (values: number[]): number | null => {
    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spinner size="lg" label="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: SPACING[32],
        textAlign: 'center',
        color: COLORS.status.error
      }}>
        <p>Error loading dashboard: {error}</p>
        <div style={{ marginTop: SPACING[16] }}>
          <Button variant="primary" onClick={fetchDashboardData}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Coaching helper functions
  const startCoaching = async (goal: string) => {
    try {
      await apiClient.post('/api/coaching/start_baseline/', { fitness_goal: goal });
      fetchDashboardData(); // Refresh
    } catch (err) {
      console.error('Failed to start coaching:', err);
    }
  };

  const activateCoaching = async () => {
    try {
      await apiClient.post('/api/coaching/activate/');
      fetchDashboardData(); // Refresh
    } catch (err) {
      console.error('Failed to activate coaching:', err);
    }
  };

  const submitQuickNutrition = async () => {
    setNutritionSaving(true);
    try {
      await apiClient.post('/api/coaching/quick_nutrition/', { ...quickNutrition, date: nutritionDate });
      fetchDashboardData(); // Refresh
    } catch (err) {
      console.error('Failed to save nutrition:', err);
    } finally {
      setNutritionSaving(false);
    }
  };

  const applyProgression = async (metric: string) => {
    try {
      await apiClient.post('/api/coaching/apply_progression/', { metric });
      fetchDashboardData(); // Refresh
    } catch (err) {
      console.error('Failed to apply progression:', err);
    }
  };

  const exportDataForAI = async () => {
    try {
      // Calculate trends and statistics
      const last7d = aggregates.slice(0, 7);
      const last30d = aggregates.slice(0, 30);
      const last90d = aggregates.slice(0, 90);

      // Helper: round to N decimals, returns number (not string) or null
      const round = (val: number | null | undefined, decimals: number = 1): number | null => {
        if (val === null || val === undefined || isNaN(val)) return null;
        const factor = Math.pow(10, decimals);
        return Math.round(val * factor) / factor;
      };

      const calculateAvg = (arr: any[], field: string): number | null => {
        const values = arr.map(a => a[field]).filter(v => v !== null && v !== undefined);
        if (values.length === 0) return null;
        // Parse values as floats to handle string decimals from API
        const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v));
        if (numericValues.length === 0) return null;
        return round(numericValues.reduce((a, b) => a + b, 0) / numericValues.length, 1);
      };

      const calculateTrend = (recent: any[], older: any[], field: string) => {
        const recentAvg = calculateAvg(recent, field);
        const olderAvg = calculateAvg(older, field);
        if (recentAvg !== null && olderAvg !== null && olderAvg !== 0) {
          const pct = ((recentAvg - olderAvg) / olderAvg) * 100;
          return { recent_avg: recentAvg, older_avg: olderAvg, change_pct: round(pct, 1) };
        }
        return null;
      };

      // Recursively remove null/undefined values, empty objects, and empty arrays
      const removeNulls = (obj: any): any => {
        if (Array.isArray(obj)) {
          const cleaned = obj.map(removeNulls).filter(v => v !== null && v !== undefined);
          return cleaned.length > 0 ? cleaned : undefined;
        }
        if (obj !== null && typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value === null || value === undefined) continue;
            const result = removeNulls(value);
            if (result === undefined) continue;
            cleaned[key] = result;
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }
        return obj;
      };

      // Carry forward sparse metrics so days without measurements show last known value
      const sparseKeys = ['body_weight_kg', 'body_fat_pct', 'vo2_max', 'waist_cm'] as const;
      const lastKnown: Record<string, number | null> = {};
      for (const key of sparseKeys) lastKnown[key] = null;
      // Scan oldest-to-newest to build carry-forward values
      const sortedForCarry = [...last90d].reverse();
      for (const agg of sortedForCarry) {
        for (const key of sparseKeys) {
          if ((agg as any)[key] != null) lastKnown[key] = (agg as any)[key];
        }
      }
      // Build a map of carry-forward values per date (oldest to newest)
      const carryForward: Record<string, Record<string, { value: number; carried_forward: boolean }>> = {};
      const running: Record<string, number | null> = {};
      for (const key of sparseKeys) running[key] = null;
      for (const agg of sortedForCarry) {
        const cf: Record<string, { value: number; carried_forward: boolean }> = {};
        for (const key of sparseKeys) {
          if ((agg as any)[key] != null) {
            running[key] = (agg as any)[key];
            cf[key] = { value: running[key]!, carried_forward: false };
          } else if (running[key] != null) {
            cf[key] = { value: running[key]!, carried_forward: true };
          }
        }
        carryForward[agg.date] = cf;
      }

      // Prepare comprehensive health data export for AI coach analysis
      const exportData = {
        ai_coaching_instructions: {
          role: "You are an AI health and fitness coach analyzing comprehensive health data.",
          focus_areas: [
            "Recovery status - Is this person ready to train hard today?",
            "Sleep quality - Are they getting enough quality sleep? Check sleep_score.",
            "Stress & Resilience - Use Oura stress/resilience signals to gauge adaptation capacity",
            "Training load - Is their exercise volume appropriate and progressive?",
            "Body composition trends - Are they moving toward their goals?",
            "Cardiovascular fitness - Is VO2max/RHR improving?",
            "Nutrition compliance - Are they hitting their nutrition targets?"
          ],
          analysis_framework: {
            immediate: "What should they do TODAY based on recovery status?",
            short_term: "What adjustments are needed THIS WEEK?",
            trends: "What patterns over the last 30-90 days need attention?",
            concerns: "Any red flags that need immediate attention?"
          },
          response_format: "Provide specific, actionable advice with clear priorities. Start with TODAY's recommendation, then weekly adjustments, then longer-term trends."
        },

        metadata: {
          export_timestamp: new Date().toISOString(),
          export_date: new Date().toISOString().split('T')[0],
          system: "FloOS Health Dashboard",
          version: "2.1",
          data_period: {
            start_date: aggregates[aggregates.length - 1]?.date || null,
            end_date: aggregates[0]?.date || null,
            total_days: aggregates.length
          }
        },

        user_profile: {
          physical_attributes: {
            height_cm: userTargets?.height_cm || null,
            height_m: userTargets?.height_cm ? round(userTargets.height_cm / 100, 2) : null,
            age: userTargets?.age || null,
            gender: userTargets?.gender || null
          },
          targets: {
            sleep_hours_per_night: userTargets?.target_sleep_hours || 8.0,
            target_weight_kg: userTargets?.target_weight_kg || null,
            weekly_workout_minutes_target: userTargets?.weekly_workout_minutes_target || 150,
            strength_sessions_per_week_target: userTargets?.strength_sessions_per_week_target || 2
          },
          data_sources: [
            "Oura Ring (sleep, readiness, HRV, stress, resilience, SpO2)",
            "Apple Health via HealthFit (steps, workouts, VO2max)",
            "Manual entries (weight, body fat, workouts)"
          ]
        },

        coaching_phase: {
          status: coachingStatus?.status || 'not_started',
          status_description: coachingStatus?.status === 'baseline_observation'
            ? "BASELINE OBSERVATION - User just started. System is collecting data to establish personal baselines. Do NOT give aggressive training or nutrition recommendations. Focus on data collection quality and general wellness."
            : coachingStatus?.status === 'active'
            ? "ACTIVE COACHING - Baseline established. User has personalized targets. Provide specific, actionable recommendations."
            : coachingStatus?.status === 'ready_for_tests'
            ? "READY FOR CAPACITY TESTS - Baseline complete, awaiting fitness assessments before full activation."
            : "NOT STARTED - User has not begun coaching. Encourage them to select a fitness goal to start.",
          fitness_goal: coachingStatus?.fitness_goal || null,
          fitness_goal_description: coachingStatus?.fitness_goal === 'fat_loss' ? "Fat Loss - prioritize sustainable caloric deficit, protect muscle mass, recovery-first approach"
            : coachingStatus?.fitness_goal === 'recomp' ? "Body Recomposition - balanced approach, slight deficit or maintenance, prioritize strength training"
            : coachingStatus?.fitness_goal === 'muscle_gain' ? "Muscle Gain - caloric surplus, prioritize progressive overload, adequate protein"
            : coachingStatus?.fitness_goal === 'maintenance' ? "Health Maintenance - sustain current fitness, focus on longevity and wellness"
            : null,
          baseline_progress: coachingStatus?.status === 'baseline_observation' && coachingStatus?.baseline_progress ? {
            days_elapsed: coachingStatus.baseline_progress.days_elapsed,
            min_days_required: coachingStatus.baseline_progress.min_days_required,
            ready_to_activate: coachingStatus.baseline_progress.ready_to_complete,
            data_completeness: coachingStatus.baseline_progress.completeness_pct,
            note: `Collecting baseline data. ${coachingStatus.baseline_progress.min_days_required - coachingStatus.baseline_progress.days_elapsed} more days minimum before coaching can activate.`
          } : null,
          active_targets: coachingStatus?.status === 'active' && coachingStatus?.targets ? {
            daily_steps: coachingStatus.targets.steps_daily,
            strength_sessions_per_week: coachingStatus.targets.strength_sessions_week,
            cardio_sessions_per_week: coachingStatus.targets.cardio_sessions_week,
            sleep_hours: coachingStatus.targets.sleep_hours
          } : null
        },

        current_status: {
          as_of_date: new Date().toISOString().split('T')[0],

          recovery: {
            score: data.recovery.score,
            score_out_of: 100,
            status: data.recovery.status,
            status_label: (() => {
              const phase = coachingStatus?.status;
              if (phase === 'baseline_observation' || phase === 'not_started') {
                return 'Moderate - Train, Don\'t Push';
              }
              return data.recovery.status === 'red' ? 'Critical - Back Off' :
                     data.recovery.status === 'yellow' ? 'Moderate - Caution' :
                     data.recovery.status === 'green' ? 'Good - Ready to Train' : 'Unknown';
            })(),
            recommendation: data.recovery.recommendation,
            components: {
              hrv: {
                score: data.recovery.components.hrv_score,
                weight: "50%",
                current_delta_pct: data.recovery.deltas.hrv_pct
              },
              resting_heart_rate: {
                score: data.recovery.components.rhr_score,
                weight: "30%",
                current_delta_bpm: data.recovery.deltas.rhr_delta
              },
              sleep: {
                score: data.recovery.components.sleep_score,
                weight: "20%",
                current_debt_minutes: data.recovery.deltas.sleep_debt_minutes
              }
            },
            penalty_breakdown: data.recovery.penalty_breakdown || [],
            total_penalty_points: data.recovery.total_penalty || 0,
            reasons: data.recovery.reasons || [],
            intensity_cap: (() => {
              const phase = coachingStatus?.status;
              const strengthBelow = data.training_load.strength_sessions_week <
                (coachingStatus?.targets?.strength_sessions_week || userTargets?.strength_sessions_per_week_target || 2);
              if (phase === 'baseline_observation' || phase === 'not_started') {
                return { max_intensity: "moderate", reason: "Baseline/observation phase - train, don't push", override: true };
              }
              if (strengthBelow) {
                return { max_intensity: "moderate", reason: "Strength sessions below target - build consistency first", override: true };
              }
              return null;
            })(),
            data_confidence: (() => {
              let confidence = "high";
              const reasons: string[] = [];
              if (data.sleep.sleep_debt !== null && data.sleep.sleep_debt > 120) {
                confidence = "medium";
                reasons.push("Sleep debt present");
              }
              if (data.nutrition.avg_score_7d === null || data.nutrition.avg_score_7d === 0) {
                confidence = "medium";
                reasons.push("No nutrition data - recovery may be affected by unknown dietary factors");
              }
              if (data.recovery.score !== null && data.recovery.score >= 70 && confidence === "medium") {
                reasons.push("Score is green but confidence reduced - recommend training with capped volume");
              }
              return { level: confidence, factors: reasons };
            })()
          },

          sleep: {
            current_7d_average_hours: data.sleep.avg_7d,
            target_hours: userTargets?.target_sleep_hours || 8.0,
            deficit_hours: data.sleep.avg_7d ? round((userTargets?.target_sleep_hours || 8.0) - data.sleep.avg_7d, 1) : null,
            accumulated_debt_minutes: data.sleep.sleep_debt,
            accumulated_debt_hours: data.sleep.sleep_debt ? round(data.sleep.sleep_debt / 60, 1) : null,
            bedtime_consistency: {
              variance_minutes: data.sleep.bedtime_variance,
              consistency_rating: data.sleep.bedtime_variance === null ? "Unknown" :
                                 data.sleep.bedtime_variance < 30 ? "Excellent" :
                                 data.sleep.bedtime_variance < 60 ? "Good" :
                                 data.sleep.bedtime_variance < 90 ? "Fair" : "Poor",
              unknown_reason: data.sleep.bedtime_variance === null ? "Insufficient bedtime data (need 2+ nights)" : null
            },
            oura_readiness_score: aggregates[0]?.readiness_score || null,
            oura_sleep_score: aggregates[0]?.sleep_score || null
          },

          oura_signals: {
            description: "Daily signals from Oura Ring (most recent day with data)",
            stress: {
              day_score: aggregates[0]?.stress_day_score || null,
              interpretation: aggregates[0]?.stress_day_score !== null && aggregates[0]?.stress_day_score !== undefined ?
                (aggregates[0].stress_day_score >= 75 ? "Restored (low stress)" :
                 aggregates[0].stress_day_score >= 25 ? "Normal" : "Stressed (high stress)") : null,
              stress_time_minutes: aggregates[0]?.stress_time_minutes || null,
              recovery_time_minutes: aggregates[0]?.recovery_time_minutes || null
            },
            resilience: {
              score: aggregates[0]?.resilience_score || null,
              interpretation: aggregates[0]?.resilience_score !== null && aggregates[0]?.resilience_score !== undefined ?
                (aggregates[0].resilience_score >= 75 ? "Solid - body adapting well" :
                 aggregates[0].resilience_score >= 50 ? "Adequate" :
                 aggregates[0].resilience_score >= 25 ? "Limited - watch for overtraining" : "Compromised - prioritize recovery") : null
            },
            blood_oxygen: {
              spo2_avg_pct: aggregates[0]?.spo2_avg ? parseFloat(aggregates[0].spo2_avg) : null,
              interpretation: aggregates[0]?.spo2_avg ?
                (parseFloat(aggregates[0].spo2_avg) >= 96 ? "Normal" :
                 parseFloat(aggregates[0].spo2_avg) >= 94 ? "Slightly low" : "Low - monitor") : null
            }
          },

          cardio_fitness: {
            resting_heart_rate: {
              current_7d_avg_bpm: data.cardio.rhr_7d_avg,
              delta_vs_30d_bpm: data.cardio.rhr_delta_30d,
              trend: data.cardio.rhr_delta_30d !== null && data.cardio.rhr_delta_30d > 0 ? "Increasing (Concerning)" :
                     data.cardio.rhr_delta_30d !== null && data.cardio.rhr_delta_30d < 0 ? "Decreasing (Good)" : "Stable"
            },
            vo2_max: {
              current_ml_kg_min: data.cardio.vo2_max_latest,
              delta_vs_90d_pct: data.cardio.vo2_max_delta_90d,
              trend: (() => {
                const d = data.cardio.vo2_max_delta_90d;
                if (d === null) return "Insufficient data";
                if (Math.abs(d) < 2) return "Stable (within noise)";
                return d > 0 ? "Improving" : "Declining";
              })(),
              fitness_level: data.cardio.vo2_max_latest !== null && data.cardio.vo2_max_latest >= 45 ? "Excellent" :
                            data.cardio.vo2_max_latest !== null && data.cardio.vo2_max_latest >= 40 ? "Good" :
                            data.cardio.vo2_max_latest !== null && data.cardio.vo2_max_latest >= 35 ? "Fair" : "Poor"
            }
          },

          training_load: {
            weekly_summary: {
              total_minutes: data.training_load.total_minutes_week,
              total_hours: round(data.training_load.total_minutes_week / 60, 1),
              target_minutes: userTargets?.weekly_workout_minutes_target || 150,
              vs_target_pct: data.training_load.total_minutes_week > 0 ?
                round((data.training_load.total_minutes_week / (userTargets?.weekly_workout_minutes_target || 150)) * 100, 0) : 0,
              hard_minutes: data.training_load.hard_minutes_week,
              easy_minutes: data.training_load.total_minutes_week - data.training_load.hard_minutes_week,
              strength_sessions: data.training_load.strength_sessions_week,
              strength_target: userTargets?.strength_sessions_per_week_target || 2,
              delta_vs_previous_week_pct: data.training_load.delta_vs_last_week,
              volume_trend: data.training_load.delta_vs_last_week !== null && data.training_load.delta_vs_last_week > 20 ? "Increasing rapidly (caution)" :
                           data.training_load.delta_vs_last_week !== null && data.training_load.delta_vs_last_week > 0 ? "Increasing" :
                           data.training_load.delta_vs_last_week !== null && data.training_load.delta_vs_last_week < -20 ? "Decreasing rapidly" :
                           data.training_load.delta_vs_last_week !== null && data.training_load.delta_vs_last_week < 0 ? "Decreasing" : "Stable"
            },
            workout_breakdown_this_week: Object.entries(data.training_load.workout_breakdown || {}).map(([type, info]: [string, any]) => ({
              type: type,
              sessions: info.count,
              total_minutes: info.total_minutes,
              avg_duration: info.count > 0 ? Math.round(info.total_minutes / info.count) : 0
            })),
            training_balance: {
              hard_to_total_ratio_pct: data.training_load.total_minutes_week > 0 ?
                round((data.training_load.hard_minutes_week / data.training_load.total_minutes_week) * 100, 1) : 0,
              recommended_hard_ratio: "20-30%",
              hard_minutes_definition: "Zone 4+ heart rate minutes (backend-computed from workout HR data)",
              data_note: data.training_load.hard_minutes_week === 0 && data.training_load.total_minutes_week > 0
                ? "WARNING: hard_minutes=0 despite logged workouts. Zone classification may be missing HR data or using Z4+ threshold. Zone 2-3 work is NOT counted as 'hard'. Consider this metric unreliable if workout types include running/cycling."
                : null,
              assessment: data.training_load.total_minutes_week === 0 ? "No data" :
                data.training_load.hard_minutes_week === 0 ? "Unknown - hard minutes not computed" :
                (data.training_load.hard_minutes_week / data.training_load.total_minutes_week) > 0.3 ?
                "Too much high intensity - add more easy/zone2 work" : "Balanced"
            }
          },

          strength: {
            top_lift: {
              exercise: data.strength.top_lift_exercise,
              e1rm_kg: data.strength.top_lift_e1rm,
              delta_4week_pct: data.strength.delta_4week_pct,
              trend: data.strength.delta_4week_pct !== null && data.strength.delta_4week_pct > 0 ? "Improving" :
                     data.strength.delta_4week_pct !== null && data.strength.delta_4week_pct < 0 ? "Declining" : "Stable"
            },
            recent_sessions: data.training_load.strength_sessions_week,
            status: data.strength.top_lift_e1rm ? "Active" : "No recent data"
          },

          body_composition: {
            weight: {
              current_7d_avg_kg: data.body_comp.weight_7d_avg,
              current_7d_avg_lbs: data.body_comp.weight_7d_avg ? round(data.body_comp.weight_7d_avg * 2.20462, 1) : null,
              delta_vs_30d_pct: data.body_comp.weight_delta_30d,
              delta_vs_30d_kg: data.body_comp.weight_7d_avg && data.body_comp.weight_delta_30d ?
                round(data.body_comp.weight_7d_avg * data.body_comp.weight_delta_30d / 100, 1) : null,
              trend: data.body_comp.weight_delta_30d !== null && data.body_comp.weight_delta_30d > 0 ? "Increasing" :
                     data.body_comp.weight_delta_30d !== null && data.body_comp.weight_delta_30d < 0 ? "Decreasing" : "Stable"
            },
            body_fat: {
              current_pct: data.body_comp.body_fat_trend,
              category: data.body_comp.body_fat_trend !== null && data.body_comp.body_fat_trend >= 25 ? "High" :
                       data.body_comp.body_fat_trend !== null && data.body_comp.body_fat_trend >= 18 ? "Moderate" :
                       data.body_comp.body_fat_trend !== null && data.body_comp.body_fat_trend >= 14 ? "Athletic" : "Very Athletic"
            },
            bmi: {
              value: data.body_comp.bmi,
              category: data.body_comp.bmi !== null && data.body_comp.bmi >= 30 ? "Obese" :
                       data.body_comp.bmi !== null && data.body_comp.bmi >= 25 ? "Overweight" :
                       data.body_comp.bmi !== null && data.body_comp.bmi >= 18.5 ? "Normal" : "Underweight",
              note: userTargets?.height_cm ? `BMI calculated from height (${userTargets.height_cm}cm) and 7d avg weight` : "BMI from 7d avg weight"
            },
            waist: {
              current_cm: data.body_comp.waist_latest,
              current_inches: data.body_comp.waist_latest ? round(data.body_comp.waist_latest / 2.54, 1) : null
            }
          },

          nutrition: {
            today: {
              score: data.nutrition.today_score,
              goal_hit: data.nutrition.goal_hit_today
            },
            last_7_days: {
              average_score: data.nutrition.avg_score_7d,
              goal_hit_days: data.nutrition.hits_7d,
              goal_hit_percentage: data.nutrition.tracked_days_7d > 0 ? round((data.nutrition.hits_7d / data.nutrition.tracked_days_7d) * 100, 0) : 0,
              alcohol_days: data.nutrition.alcohol_days_7d,
              late_eating_days: data.nutrition.late_eat_days_7d
            },
            compliance: {
              rating: (() => {
                const hasData = data.nutrition.avg_score_7d !== null && data.nutrition.avg_score_7d > 0;
                if (!hasData) return "Unknown - no nutrition data logged";
                const hitPct = data.nutrition.tracked_days_7d > 0 ? data.nutrition.hits_7d / data.nutrition.tracked_days_7d : 0;
                if (hitPct >= 0.85) return "Excellent";
                if (hitPct >= 0.6) return "Good";
                if (hitPct >= 0.3) return "Fair";
                return "Poor";
              })(),
              data_days: data.nutrition.tracked_days_7d,
              sufficient_data: (data.nutrition.avg_score_7d !== null && data.nutrition.avg_score_7d > 0)
            }
          }
        },

        trends_analysis: {
          time_periods: {
            "7_day": { start: last7d[6]?.date, end: last7d[0]?.date, days: 7 },
            "30_day": { start: last30d[29]?.date, end: last30d[0]?.date, days: 30 },
            "90_day": { start: last90d[89]?.date, end: last90d[0]?.date, days: 90 }
          },

          sleep_trends: {
            "7d_avg_hours": (() => { const avg = calculateAvg(last7d, 'sleep_duration_minutes'); return avg !== null ? round(avg / 60, 1) : null; })(),
            "30d_avg_hours": (() => { const avg = calculateAvg(last30d, 'sleep_duration_minutes'); return avg !== null ? round(avg / 60, 1) : null; })(),
            "90d_avg_hours": (() => { const avg = calculateAvg(last90d, 'sleep_duration_minutes'); return avg !== null ? round(avg / 60, 1) : null; })(),
            trend_7d_vs_30d: calculateTrend(last7d, last30d.slice(7), 'sleep_duration_minutes')
          },

          hrv_trends: {
            "7d_avg_ms": calculateAvg(last7d, 'hrv_rmssd'),
            "30d_avg_ms": calculateAvg(last30d, 'hrv_rmssd'),
            "90d_avg_ms": calculateAvg(last90d, 'hrv_rmssd'),
            trend_7d_vs_30d: calculateTrend(last7d, last30d.slice(7), 'hrv_rmssd')
          },

          rhr_trends: {
            "7d_avg_bpm": calculateAvg(last7d, 'resting_hr'),
            "30d_avg_bpm": calculateAvg(last30d, 'resting_hr'),
            "90d_avg_bpm": calculateAvg(last90d, 'resting_hr'),
            trend_7d_vs_30d: calculateTrend(last7d, last30d.slice(7), 'resting_hr')
          },

          activity_trends: {
            steps: {
              "7d_avg": calculateAvg(last7d, 'steps'),
              "30d_avg": calculateAvg(last30d, 'steps'),
              trend_7d_vs_30d: calculateTrend(last7d, last30d.slice(7), 'steps')
            },
            workout_minutes: {
              "7d_total": last7d.reduce((sum, a) => sum + (a.workout_minutes || 0), 0),
              "30d_total": last30d.reduce((sum, a) => sum + (a.workout_minutes || 0), 0),
              "7d_avg_per_day": round(last7d.reduce((sum, a) => sum + (a.workout_minutes || 0), 0) / 7, 0),
              "30d_avg_per_day": round(last30d.reduce((sum, a) => sum + (a.workout_minutes || 0), 0) / 30, 0)
            }
          },

          weight_trends: {
            "7d_avg_kg": calculateAvg(last7d, 'body_weight_kg'),
            "30d_avg_kg": calculateAvg(last30d, 'body_weight_kg'),
            "90d_avg_kg": calculateAvg(last90d, 'body_weight_kg'),
            trend_7d_vs_30d: calculateTrend(last7d, last30d.slice(7), 'body_weight_kg')
          }
        },

        data_quality: {
          description: "Summary of data availability and sources",
          sleep_data_days: last90d.filter(a => a.sleep_duration_minutes).length,
          hrv_data_days: last90d.filter(a => a.hrv_rmssd).length,
          steps_data_days: last90d.filter(a => a.steps).length,
          weight_data_days: last90d.filter(a => a.body_weight_kg).length,
          workout_data_days: last90d.filter(a => a.workout_minutes && a.workout_minutes > 0).length,
          readiness_data_days: last90d.filter(a => a.readiness_score).length,
          sparse_metrics_note: "Some metrics (weight, body fat, waist) are measured less frequently. Use most recent values for current status."
        },

        daily_history: {
          description: "Daily health metrics for the last 90 days (only days with data shown)",
          total_days_in_period: last90d.length,
          days_with_data: last90d.filter(a => a.sleep_duration_minutes || a.steps || a.hrv_rmssd || a.workout_minutes).length,
          data: last90d
            .filter(agg => agg.sleep_duration_minutes || agg.steps || agg.hrv_rmssd || agg.workout_minutes || agg.body_weight_kg)
            .map(agg => ({
              date: agg.date,
              sleep: agg.sleep_duration_minutes ? {
                duration_hours: round(agg.sleep_duration_minutes / 60, 1),
                sleep_score: agg.sleep_score,
                efficiency_pct: agg.sleep_efficiency_pct,
                rem_minutes: agg.rem_sleep_minutes,
                deep_minutes: agg.deep_sleep_minutes,
                debt_minutes: agg.sleep_debt_minutes
              } : null,
              recovery: (agg.hrv_rmssd || agg.readiness_score || agg.stress_day_score || agg.resilience_score) ? {
                hrv_rmssd: agg.hrv_rmssd,
                resting_hr: agg.resting_hr,
                readiness_score: agg.readiness_score,
                // Oura Stress metrics (100=Restored, 50=Normal, 0=Stressed)
                stress_day_score: agg.stress_day_score,
                stress_time_minutes: agg.stress_time_minutes,
                recovery_time_minutes: agg.recovery_time_minutes,
                // Oura Resilience (100=Solid, 75=Adequate, 50=Limited, 25=Compromised)
                resilience_score: agg.resilience_score,
                // Blood oxygen
                spo2_avg: agg.spo2_avg
              } : null,
              activity: (agg.steps || agg.workout_minutes) ? {
                steps: agg.steps,
                workout_minutes: agg.workout_minutes,
                zone2_minutes: agg.zone2_minutes
              } : null,
              body: (() => {
                const cf = carryForward[agg.date] || {};
                const w = cf.body_weight_kg;
                const bf = cf.body_fat_pct;
                if (!w) return null;
                return {
                  weight_kg: w.value,
                  ...(w.carried_forward ? { weight_carried_forward: true } : {}),
                  ...(bf ? { body_fat_pct: bf.value, ...(bf.carried_forward ? { body_fat_pct_carried_forward: true } : {}) } : {})
                };
              })(),
              vo2_max: (() => {
                const cf = carryForward[agg.date]?.vo2_max;
                if (!cf) return null;
                return cf.carried_forward ? { value: cf.value, _carried_forward: true } : cf.value;
              })()
            }))
        },

        insights: {
          recovery_concerns: [
            data.recovery.score !== null && data.recovery.score < 30 && "CRITICAL: Recovery score very low - immediate rest recommended",
            data.recovery.deltas.hrv_pct !== null && data.recovery.deltas.hrv_pct < -20 && "HRV significantly suppressed - sign of stress/overtraining",
            data.recovery.deltas.rhr_delta !== null && data.recovery.deltas.rhr_delta > 5 && "Resting heart rate elevated - possible illness or overtraining",
            data.sleep.sleep_debt !== null && data.sleep.sleep_debt > 300 && "Significant sleep debt accumulated - prioritize sleep"
          ].filter(Boolean),

          training_concerns: [
            data.training_load.total_minutes_week < (userTargets?.weekly_workout_minutes_target || 150) &&
              `Weekly activity (${data.training_load.total_minutes_week}min) below target (${userTargets?.weekly_workout_minutes_target || 150}min)`,
            data.training_load.delta_vs_last_week !== null && data.training_load.delta_vs_last_week > 30 && "Training load increased >30% - risk of overtraining",
            data.training_load.strength_sessions_week < (userTargets?.strength_sessions_per_week_target || 2) &&
              `Only ${data.training_load.strength_sessions_week} strength session(s) this week (target: ${userTargets?.strength_sessions_per_week_target || 2})`
          ].filter(Boolean),

          positive_trends: [
            data.cardio.vo2_max_delta_90d !== null && data.cardio.vo2_max_delta_90d > 5 && "VO2max improving significantly (+5%)",
            data.sleep.bedtime_variance !== null && data.sleep.bedtime_variance < 30 && "Excellent sleep schedule consistency",
            data.nutrition.tracked_days_7d > 0 && (data.nutrition.hits_7d / data.nutrition.tracked_days_7d) >= 0.85 && "Nutrition goals consistently met",
            data.training_load.total_minutes_week >= (userTargets?.weekly_workout_minutes_target || 150) &&
              `Weekly activity target met (${data.training_load.total_minutes_week}/${userTargets?.weekly_workout_minutes_target || 150}min)`
          ].filter(Boolean)
        },

        recommendations: {
          immediate_actions: [
            data.recovery.score !== null && data.recovery.score < 30 && "Take rest day - recovery score critical",
            data.sleep.sleep_debt !== null && data.sleep.sleep_debt > 300 && `Sleep debt high (${round(data.sleep.sleep_debt / 60, 1)}h) - add 30-60min tonight`,
            data.recovery.deltas.hrv_pct !== null && data.recovery.deltas.hrv_pct < -30 && "HRV very low - consider stress management/meditation"
          ].filter(Boolean),

          weekly_goals: [
            data.training_load.total_minutes_week < (userTargets?.weekly_workout_minutes_target || 150) &&
              `Increase weekly activity to ${userTargets?.weekly_workout_minutes_target || 150}+ minutes`,
            data.training_load.strength_sessions_week < (userTargets?.strength_sessions_per_week_target || 2) &&
              `Add ${(userTargets?.strength_sessions_per_week_target || 2) - data.training_load.strength_sessions_week} more strength session(s) this week`,
            data.sleep.avg_7d !== null && data.sleep.avg_7d < (userTargets?.target_sleep_hours || 8) - 1 &&
              `Increase sleep by ${round((userTargets?.target_sleep_hours || 8) - data.sleep.avg_7d, 1)}h to reach ${userTargets?.target_sleep_hours || 8}h target`
          ].filter(Boolean)
        }
      };

      // Copy to clipboard (with fallback for non-secure contexts)
      const jsonString = JSON.stringify(removeNulls(exportData), null, 2);

      try {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(jsonString);
        } else {
          // Fallback for non-HTTPS contexts
          const textarea = document.createElement('textarea');
          textarea.value = jsonString;
          textarea.style.position = 'fixed';
          textarea.style.left = '-9999px';
          textarea.style.top = '-9999px';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();

          const successful = document.execCommand('copy');
          document.body.removeChild(textarea);

          if (!successful) {
            throw new Error('execCommand failed');
          }
        }

        // Show success message with stats
        const daysWithData = exportData.daily_history.days_with_data;
        const concernsCount = exportData.insights.recovery_concerns.length + exportData.insights.training_concerns.length;
        const workoutTypes = exportData.current_status.training_load.workout_breakdown_this_week.length;

        alert(`✅ Health data exported for AI coach!\n\n` +
              `📊 Export includes:\n` +
              `• AI coaching instructions & analysis framework\n` +
              `• Current status across 7 health categories\n` +
              `• ${daysWithData} days with data (of ${last90d.length} day period)\n` +
              `• ${workoutTypes} workout type(s) this week\n` +
              `• Trend analysis (7d, 30d, 90d)\n` +
              `• ${concernsCount} concern(s), ${exportData.insights.positive_trends.length} positive trend(s)\n` +
              `• Data quality summary\n\n` +
              `Paste into Claude, ChatGPT, or your AI coach for personalized analysis!`);
      } catch (copyErr) {
        console.error('Failed to copy to clipboard:', copyErr);
        // If copy fails, offer download instead
        alert('⚠️ Could not copy to clipboard. Downloading as file instead...');

        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `health-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to export data:', err);
      alert('❌ Failed to export data: ' + err);
    }
  };

  return (
    <div style={{ padding: SPACING[24] }}>
      {/* Coaching Onboarding Banner */}
      {coachingStatus?.status === 'not_started' && (
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.accent.default}22, ${COLORS.accent.default}11)`,
          border: `1px solid ${COLORS.accent.default}44`,
          borderRadius: RADIUS,
          padding: SPACING[24],
          marginBottom: SPACING[24]
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12], marginBottom: SPACING[16] }}>
            <SparklesIcon style={{ width: 24, height: 24, color: COLORS.accent.default }} />
            <h2 style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: 600, color: COLORS.text.primary, margin: 0 }}>
              Start AI Coaching
            </h2>
          </div>
          <p style={{ color: COLORS.text.secondary, marginBottom: SPACING[16], fontSize: TYPOGRAPHY.size.lg }}>
            Select your fitness goal to begin a 7-14 day baseline observation. We'll learn your patterns before setting personalized targets.
          </p>
          <div style={{ display: 'flex', gap: SPACING[12], flexWrap: 'wrap' }}>
            {[
              { id: 'fat_loss', label: 'Fat Loss', emoji: '🔥' },
              { id: 'recomp', label: 'Recomposition', emoji: '💪' },
              { id: 'muscle_gain', label: 'Muscle Gain', emoji: '🏋️' },
              { id: 'maintenance', label: 'Maintenance', emoji: '⚖️' }
            ].map(goal => (
              <button
                key={goal.id}
                onClick={() => startCoaching(goal.id)}
                style={{
                  padding: `${SPACING[12]} ${SPACING[24]}`,
                  background: COLORS.bg.panel,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  cursor: 'pointer',
                  fontSize: TYPOGRAPHY.size.lg,
                  color: COLORS.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[8]
                }}
              >
                <span>{goal.emoji}</span>
                {goal.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Baseline Observation Banner */}
      {coachingStatus?.status === 'baseline_observation' && coachingStatus.baseline_progress && (
        <div style={{
          background: COLORS.bg.panel,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          padding: SPACING[24],
          marginBottom: SPACING[24]
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING[16] }}>
            <div>
              <h3 style={{ fontSize: TYPOGRAPHY.size.xl, fontWeight: 600, color: COLORS.text.primary, margin: 0, marginBottom: SPACING[4] }}>
                Baseline Observation
              </h3>
              <p style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.size.base, margin: 0 }}>
                Day {coachingStatus.baseline_progress.days_elapsed} of {coachingStatus.baseline_progress.min_days_required}+ • Goal: {coachingStatus.fitness_goal?.replace('_', ' ')}
              </p>
            </div>
            {coachingStatus.baseline_progress.ready_to_complete && (
              <button
                onClick={activateCoaching}
                style={{
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  background: COLORS.status.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: RADIUS,
                  cursor: 'pointer',
                  fontSize: TYPOGRAPHY.size.base,
                  fontWeight: 600
                }}
              >
                Activate Coaching
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: SPACING[16], flexWrap: 'wrap' }}>
            {Object.entries(coachingStatus.baseline_progress.completeness_pct).map(([metric, pct]) => (
              <div key={metric} style={{ minWidth: '80px' }}>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary, textTransform: 'uppercase', marginBottom: SPACING[4] }}>
                  {metric}
                </div>
                <div style={{
                  height: '4px',
                  background: COLORS.bg.base,
                  borderRadius: RADIUS,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(100, pct)}%`,
                    background: pct >= 100 ? COLORS.status.success : COLORS.accent.default,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <div style={{ fontSize: TYPOGRAPHY.size.md, color: pct >= 100 ? COLORS.status.success : COLORS.text.secondary, marginTop: SPACING[4] }}>
                  {Math.round(pct)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Nutrition (4 Yes/No) - Always visible */}
      {(
        <div style={{
          background: COLORS.bg.panel,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          padding: SPACING[24],
          marginBottom: SPACING[24]
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING[16] }}>
            <h3 style={{ fontSize: TYPOGRAPHY.size.lg, fontWeight: 600, color: COLORS.text.primary, margin: 0 }}>
              {nutritionDate === todayStr ? "Today's Nutrition" : `Nutrition — ${nutritionDate}`}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
              <input
                type="date"
                value={nutritionDate}
                max={todayStr}
                onChange={e => setNutritionDate(e.target.value)}
                style={{
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.primary,
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>4 quick questions</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACING[12] }}>
            {[
              { key: 'protein_hit', label: 'Protein at every meal?', subtitle: 'A real protein source at each meal you eat today' },
              { key: 'energy_intent_hit', label: 'Ate to plan?', subtitle: 'Reasonable portions, no junk food, no late-night eating' },
              { key: 'no_alcohol', label: 'Zero alcohol?', subtitle: 'No alcoholic drinks at all today' },
              { key: 'plants_hit', label: '3+ servings of plants?', subtitle: '3+ servings of vegetables, fruit, or legumes (not potatoes/fries)' }
            ].map(item => {
              const value = quickNutrition[item.key as keyof typeof quickNutrition];
              return (
                <div key={item.key} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.primary, marginBottom: SPACING[4], fontWeight: 500 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginBottom: SPACING[8], lineHeight: 1.3 }}>
                    {item.subtitle}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING[8] }}>
                    <button
                      onClick={() => setQuickNutrition(prev => ({ ...prev, [item.key]: true }))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: value === true ? `2px solid ${COLORS.status.success}` : `1px solid ${COLORS.border.default}`,
                        background: value === true ? `${COLORS.status.success}22` : COLORS.bg.base,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CheckCircleIcon style={{ width: 18, height: 18, color: value === true ? COLORS.status.success : COLORS.text.tertiary }} />
                    </button>
                    <button
                      onClick={() => setQuickNutrition(prev => ({ ...prev, [item.key]: false }))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: value === false ? `2px solid ${COLORS.status.error}` : `1px solid ${COLORS.border.default}`,
                        background: value === false ? `${COLORS.status.error}22` : COLORS.bg.base,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <XCircleIcon style={{ width: 18, height: 18, color: value === false ? COLORS.status.error : COLORS.text.tertiary }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {Object.values(quickNutrition).some(v => v !== null) && (
            <div style={{ marginTop: SPACING[16], textAlign: 'center' }}>
              <button
                onClick={submitQuickNutrition}
                disabled={nutritionSaving}
                style={{
                  padding: `${SPACING[8]} ${SPACING[24]}`,
                  background: COLORS.accent.default,
                  color: COLORS.bg.base,
                  border: 'none',
                  borderRadius: RADIUS,
                  cursor: nutritionSaving ? 'not-allowed' : 'pointer',
                  fontSize: TYPOGRAPHY.size.base,
                  fontWeight: 600,
                  opacity: nutritionSaving ? 0.6 : 1
                }}
              >
                {nutritionSaving ? 'Saving...' : 'Save Nutrition'}
              </button>
            </div>
          )}
        </div>
      )}


      {/* Export Button */}
      <div style={{ marginBottom: SPACING[16], display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={exportDataForAI}
          style={{
            padding: `${SPACING[8]} ${SPACING[16]}`,
            background: COLORS.accent.default,
            color: COLORS.bg.base,
            border: 'none',
            borderRadius: RADIUS,
            cursor: 'pointer',
            fontSize: TYPOGRAPHY.size.base,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: SPACING[8]
          }}
        >
          📊 Export for AI Coach
        </button>
      </div>

      {/* 9-Tile Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: SPACING[16],
        marginTop: SPACING[24]
      }}>
        {/* Row 1: Daily check — How am I today? */}
        <RecoveryTile data={data.recovery} aggregates={aggregates} onClick={() => setRecoveryModalOpen(true)} />
        <SleepTile data={data.sleep} aggregates={aggregates} onClick={() => setSleepModalOpen(true)} />
        <NutritionTile data={data.nutrition} />
        {/* Row 2: Daily action — What did I do / log? */}
        <TrainingLoadTile data={data.training_load} aggregates={aggregates} onOpenForm={onOpenWorkoutForm} onClick={() => setWorkoutModalOpen(true)} userAge={userTargets?.age} stepsGoal={coachingStatus?.targets?.steps_daily} />
        <StrengthTile data={data.strength} onOpenForm={onOpenStrengthForm} />
        <BodyCompTile data={data.body_comp} onOpenForm={onOpenBodyCompForm} onClick={() => setWeightModalOpen(true)} weightGoal={weightGoal} aggregates={aggregates} />
        {/* Row 3: Long-term analytics */}
        <CardioTile data={data.cardio} aggregates={aggregates} onClick={() => setCardioRHRModalOpen(true)} onClickVO2={() => setCardioVO2ModalOpen(true)} />
        <StressTile aggregates={aggregates} onClick={() => setStressModalOpen(true)} />
        <PhysiologyTile data={physiologyData} />
      </div>

      {/* History Modals */}
      <MetricHistoryModal
        isOpen={recoveryModalOpen}
        onClose={() => setRecoveryModalOpen(false)}
        title="Readiness Score (Oura)"
        metricKey="readiness_score"
        data={aggregates}
        unit="/ 100"
        color={COLORS.accent.default}
        targetValue={processGoals.readiness_score}
      />

      <MetricHistoryModal
        isOpen={sleepModalOpen}
        onClose={() => setSleepModalOpen(false)}
        title="Sleep Duration"
        metricKey="sleep_duration_minutes"
        data={aggregates}
        unit=""
        color={COLORS.accent.default}
        targetValue={processGoals.sleep_hours ? Math.round(processGoals.sleep_hours * 60) : undefined}
        secondaryMetric={{
          key: 'sleep_debt_minutes',
          label: 'Sleep Debt',
          color: COLORS.status.error,
          unit: ''
        }}
      />

      <MetricHistoryModal
        isOpen={cardioRHRModalOpen}
        onClose={() => setCardioRHRModalOpen(false)}
        title="Resting Heart Rate"
        metricKey="resting_hr"
        data={aggregates}
        unit="bpm"
        color={COLORS.status.info}
      />

      <MetricHistoryModal
        isOpen={cardioVO2ModalOpen}
        onClose={() => setCardioVO2ModalOpen(false)}
        title="VO2 Max"
        metricKey="vo2_max"
        data={aggregates}
        unit="ml/kg/min"
        color={COLORS.status.success}
      />

      <MetricHistoryModal
        isOpen={weightModalOpen}
        onClose={() => setWeightModalOpen(false)}
        title="Body Weight"
        metricKey="body_weight_kg"
        data={aggregates}
        unit="kg"
        color={COLORS.text.secondary}
        autoScaleY={true}
      />

      <MetricHistoryModal
        isOpen={hrvModalOpen}
        onClose={() => setHRVModalOpen(false)}
        title="Heart Rate Variability (HRV)"
        metricKey="hrv_rmssd"
        data={aggregates}
        unit="ms"
        color={COLORS.status.success}
      />

      <MetricHistoryModal
        isOpen={workoutModalOpen}
        onClose={() => setWorkoutModalOpen(false)}
        title="Daily Steps"
        metricKey="steps"
        data={aggregates}
        unit="steps"
        color={COLORS.status.warning}
        targetValue={processGoals.steps}
      />

      <MetricHistoryModal
        isOpen={stressModalOpen}
        onClose={() => setStressModalOpen(false)}
        title="Stress Time (Oura)"
        metricKey="stress_time_minutes"
        data={aggregates}
        unit="min"
        color={COLORS.status.error}
        secondaryMetric={{
          key: 'recovery_time_minutes',
          label: 'Recovery Time',
          color: COLORS.status.success,
          unit: 'min'
        }}
      />
    </div>
  );
};

// Sparkline Component

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 60,
  height = 20,
  color = COLORS.accent.default
}) => {
  if (data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Helper: Format decimal hours as "Xh Ym"
const formatHoursMinutes = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Tile Components

const RecoveryTile: React.FC<{ data: DashboardData['recovery']; aggregates: any[]; onClick?: () => void }> = ({ data, aggregates, onClick }) => {
  const [showExplanation, setShowExplanation] = React.useState(false);

  // Check if data is stale (not from today)
  const today = new Date().toISOString().split('T')[0];
  const dataDate = data.date;
  const isStale = dataDate && dataDate !== today;
  const daysOld = isStale ? Math.floor((new Date(today).getTime() - new Date(dataDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;

  // Get SpO2 from today's aggregate
  const todayAgg = aggregates[0] || {};
  const spo2 = todayAgg.spo2_avg ? parseFloat(todayAgg.spo2_avg) : null;

  const statusColors = {
    green: { bg: 'rgba(34, 197, 94, 0.1)', border: 'rgb(34, 197, 94)', text: 'rgb(34, 197, 94)' },
    yellow: { bg: 'rgba(234, 179, 8, 0.1)', border: 'rgb(234, 179, 8)', text: 'rgb(234, 179, 8)' },
    red: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgb(239, 68, 68)', text: 'rgb(239, 68, 68)' },
    unknown: { bg: COLORS.bg.base, border: COLORS.border.default, text: COLORS.text.secondary }
  };

  const colors = statusColors[data.status];

  return (
    <Tile
      icon={<HeartIcon style={{ width: 20, height: 20 }} />}
      title="Recovery Index"
      iconColor={colors.text}
      onClick={onClick}
    >
      {data.score !== null ? (
        <>
          {/* Score - COMPACT */}
          <div style={{ fontSize: TYPOGRAPHY.size['7xl'], fontWeight: 600, color: colors.text }}>
            {Math.round(data.score)}
            <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>/ 100</span>
          </div>

          {/* Recommendation - COMPACT */}
          <div style={{
            fontSize: TYPOGRAPHY.size.md,
            color: colors.text,
            fontWeight: 500,
            marginTop: SPACING[4]
          }}>
            {data.recommendation}
          </div>

          {/* Missing metrics warning */}
          {data.components_has_data && (
            !data.components_has_data.hrv || !data.components_has_data.rhr || !data.components_has_data.sleep
          ) && (
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.status.warning,
              marginTop: SPACING[4]
            }}>
              Missing: {[
                !data.components_has_data.hrv && 'HRV',
                !data.components_has_data.rhr && 'RHR',
                !data.components_has_data.sleep && 'Sleep',
              ].filter(Boolean).join(', ')} (scored as neutral 50)
            </div>
          )}

          {/* Stale data warning */}
          {isStale && (
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.status.warning,
              marginTop: SPACING[4]
            }}>
              ⚠️ Data from {daysOld} day{daysOld > 1 ? 's' : ''} ago - sync HealthFit for latest
            </div>
          )}

          {/* SpO2 Indicator */}
          {spo2 && (
            <div style={{ marginTop: SPACING[12], fontSize: TYPOGRAPHY.size.sm }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>SpO₂</div>
              <div style={{
                fontWeight: 600,
                color: spo2 >= 95 ? COLORS.status.success :
                       spo2 < 92 ? COLORS.status.error : COLORS.status.warning
              }}>
                {spo2.toFixed(1)}%
              </div>
            </div>
          )}

          {/* Penalty Breakdown - 2-COLUMN GRID */}
          {data.penalty_breakdown && data.penalty_breakdown.length > 0 && (
            <div style={{
              background: COLORS.bg.base,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[8],
              marginTop: SPACING[12]
            }}>
              <div style={{
                fontSize: TYPOGRAPHY.size.xs,
                fontWeight: 600,
                color: COLORS.text.tertiary,
                marginBottom: SPACING[8]
              }}>
                WHY TODAY:
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: SPACING[8],
                fontSize: TYPOGRAPHY.size.sm
              }}>
                {data.penalty_breakdown.map((penalty: any, i: number) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: TYPOGRAPHY.size.sm }}>• {penalty.description}</span>
                    <span style={{
                      color: COLORS.status.error,
                      fontWeight: 600,
                      fontSize: TYPOGRAPHY.size.xs
                    }}>
                      −{penalty.penalty_points}
                    </span>
                  </div>
                ))}
              </div>

              {data.total_penalty !== undefined && data.total_penalty > 0 && (
                <div style={{
                  fontSize: TYPOGRAPHY.size.xs,
                  color: COLORS.text.secondary,
                  marginTop: SPACING[8],
                  paddingTop: SPACING[8],
                  borderTop: `1px solid ${COLORS.border.default}`,
                  fontStyle: 'italic',
                  textAlign: 'right'
                }}>
                  Total: −{data.total_penalty} pts
                </div>
              )}
            </div>
          )}

          {/* Help toggle - COMPACT */}
          <button
            onClick={() => setShowExplanation(!showExplanation)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.accent.default,
              padding: 0,
              marginTop: SPACING[8]
            }}
          >
            {showExplanation ? '− Hide' : '? How calculated'}
          </button>

          {showExplanation && (
            <div style={{
              background: COLORS.bg.base,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[8],
              marginTop: SPACING[8],
              fontSize: TYPOGRAPHY.size.xs,
              lineHeight: 1.4
            }}>
              <strong>Recovery Index:</strong> 0–100 readiness score.
              <br />
              <strong>Formula:</strong> 100 − penalties
              <br />
              • HRV: 50% | RHR: 30% | Sleep: 20%
            </div>
          )}
        </>
      ) : (
        // Fallback: Show Oura data when recovery score is unavailable
        (spo2 || todayAgg.readiness_score) ? (
          <>
            {/* Oura Readiness as primary fallback */}
            {todayAgg.readiness_score && (
              <div style={{ marginBottom: SPACING[12] }}>
                <div style={{ fontSize: TYPOGRAPHY.size['6xl'], fontWeight: 600, color: COLORS.text.primary }}>
                  {todayAgg.readiness_score}
                  <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>/ 100</span>
                </div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
                  Oura Readiness
                </div>
              </div>
            )}

            {/* SpO2 Indicator */}
            {spo2 && (
              <div style={{ fontSize: TYPOGRAPHY.size.sm }}>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>SpO₂</div>
                <div style={{
                  fontWeight: 600,
                  color: spo2 >= 95 ? COLORS.status.success :
                         spo2 < 92 ? COLORS.status.error : COLORS.status.warning
                }}>
                  {spo2.toFixed(1)}%
                </div>
              </div>
            )}

            <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[12] }}>
              {data.recommendation || '⚠️ Sync Oura to get full recovery score (needs HRV, RHR, sleep data)'}
            </div>
          </>
        ) : (
          <EmptyState
            message="No recovery data"
            action="Sync device"
          />
        )
      )}
    </Tile>
  );
};

const StressTile: React.FC<{ aggregates: any[]; onClick?: () => void }> = ({ aggregates, onClick }) => {
  const todayAgg = aggregates[0] || {};
  const stressScore = todayAgg.stress_day_score;
  const resilienceScore = todayAgg.resilience_score;
  const recoveryTime = todayAgg.recovery_time_minutes;
  const stressTime = todayAgg.stress_time_minutes;
  const sleepRecovery = todayAgg.sleep_recovery_score ? parseFloat(todayAgg.sleep_recovery_score) : null;

  const stressLabel = stressScore === 100 ? 'Restored' : stressScore === 50 ? 'Normal' : stressScore === 0 ? 'Stressed' : null;
  const resilienceLabel = resilienceScore === 100 ? 'Solid' : resilienceScore === 75 ? 'Adequate' : resilienceScore === 50 ? 'Limited' : resilienceScore === 25 ? 'Compromised' : null;

  const stressColor = stressScore === 100 ? COLORS.status.success :
                      stressScore === 0 ? COLORS.status.error : COLORS.status.warning;
  const resilienceColor = resilienceScore >= 75 ? COLORS.status.success :
                          resilienceScore <= 25 ? COLORS.status.error : COLORS.text.primary;

  const hasData = stressLabel || resilienceLabel;

  return (
    <Tile
      icon={<ShieldExclamationIcon style={{ width: 20, height: 20 }} />}
      title="Stress & Resilience"
      iconColor={stressColor}
      onClick={onClick}
    >
      {hasData ? (
        <>
          {/* Main stress status */}
          {stressLabel && (
            <div style={{
              fontSize: TYPOGRAPHY.size['3xl'],
              fontWeight: 600,
              color: stressColor
            }}>
              {stressLabel}
            </div>
          )}

          {/* 2x2 detail grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING[8],
            marginTop: SPACING[12]
          }}>
            {resilienceLabel && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>Resilience</div>
                <div style={{ fontWeight: 600, color: resilienceColor }}>
                  {resilienceLabel}
                </div>
              </div>
            )}
            {stressTime !== null && stressTime !== undefined && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>Stress Time</div>
                <div style={{ fontWeight: 600, color: COLORS.text.primary }}>
                  {stressTime} min
                </div>
              </div>
            )}
            {recoveryTime !== null && recoveryTime !== undefined && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>Recovery Time</div>
                <div style={{ fontWeight: 600, color: COLORS.text.primary }}>
                  {recoveryTime} min
                </div>
              </div>
            )}
            {sleepRecovery !== null && (
              <div>
                <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>Sleep Recovery</div>
                <div style={{
                  fontWeight: 600,
                  color: sleepRecovery >= 75 ? COLORS.status.success :
                         sleepRecovery < 50 ? COLORS.status.error : COLORS.text.primary
                }}>
                  {Math.round(sleepRecovery)}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <EmptyState message="No stress data" action="Sync Oura" />
      )}
    </Tile>
  );
};

const SleepTile: React.FC<{
  data: DashboardData['sleep'];
  aggregates: any[];
  onClick?: () => void;
}> = ({ data, aggregates, onClick }) => {
  // Calculate 7-day sparkline data
  const last7d = aggregates.slice(0, 7).reverse();
  const sleepHours = last7d
    .map(a => a.sleep_duration_minutes ? a.sleep_duration_minutes / 60 : null)
    .filter((v): v is number => v !== null);

  // Get Oura sleep score (from latest aggregate) - this is the dedicated sleep quality score
  const sleepScore = aggregates[0]?.sleep_score || null;

  return (
    <Tile
      icon={<MoonIcon style={{ width: 20, height: 20 }} />}
      title="Sleep"
      iconColor={COLORS.accent.default}
      onClick={onClick}
    >
      {data.avg_7d !== null ? (
        <>
          {/* Primary metric with sparkline */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: TYPOGRAPHY.size['6xl'], fontWeight: 600, color: COLORS.text.primary }}>
                {formatHoursMinutes(data.avg_7d)}
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                avg 7d
              </div>
            </div>

            {sleepHours.length >= 2 && (
              <div>
                <Sparkline data={sleepHours} width={50} height={24} />
                <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, textAlign: 'right', marginTop: SPACING[4] }}>
                  7d trend
                </div>
              </div>
            )}
          </div>

          {/* Secondary metrics - COMPACT GRID */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING[8],
            marginTop: SPACING[12],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            {sleepScore && (
              <div>
                <span style={{ color: COLORS.text.tertiary }}>Score: </span>
                <span style={{ fontWeight: 600 }}>{sleepScore}</span>
              </div>
            )}

            {data.bedtime_variance !== null && (
              <div>
                <span style={{ color: COLORS.text.tertiary }}>Variance: </span>
                <span style={{ fontWeight: 600 }}>±{Math.round(data.bedtime_variance)}m</span>
              </div>
            )}

            {data.sleep_debt !== null && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: COLORS.text.tertiary }}>Debt: </span>
                <span style={{
                  fontWeight: 600,
                  color: data.sleep_debt > 120 ? COLORS.status.error : COLORS.text.primary
                }}>
                  {Math.round(data.sleep_debt)}min
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <EmptyState
          message="No sleep data"
          action="Sync device"
        />
      )}
    </Tile>
  );
};

const NutritionTile: React.FC<{ data: DashboardData['nutrition']; onClick?: () => void }> = ({ data, onClick }) => {
  return (
    <Tile
      icon={<FireIcon style={{ width: 20, height: 20 }} />}
      title="Nutrition"
      iconColor={COLORS.status.warning}
      onClick={onClick}
    >
      {data.today_score !== null ? (
        <>
          <div style={{ fontSize: TYPOGRAPHY.size['6xl'], fontWeight: 600, color: COLORS.text.primary }}>
            {Math.round(data.today_score)}
          </div>
          <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
            today's score
          </div>
          {data.tracked_days_7d < 7 && (
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.status.warning,
              marginTop: SPACING[8],
              padding: `${SPACING[4]}px ${SPACING[8]}px`,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderRadius: RADIUS,
            }}>
              {7 - data.tracked_days_7d} of 7 days not logged
            </div>
          )}
          <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginTop: SPACING[12] }}>
            <div>Hits: {data.hits_7d}/{data.tracked_days_7d} tracked days</div>
            {data.avg_score_7d !== null && (
              <div style={{ marginTop: SPACING[8] }}>
                Avg 7d: {Math.round(data.avg_score_7d)}
              </div>
            )}
            <div style={{ marginTop: SPACING[8] }}>
              Alcohol: {data.alcohol_days_7d}d | Late: {data.late_eat_days_7d}d
            </div>
          </div>
        </>
      ) : (
        <EmptyState
          message="No nutrition data"
          action="Use quick nutrition above"
        />
      )}
    </Tile>
  );
};

const CardioTile: React.FC<{
  data: DashboardData['cardio'];
  aggregates: any[];
  onClick?: () => void;
  onClickVO2?: () => void;
}> = ({ data, aggregates, onClick, onClickVO2 }) => {
  // Calculate VO2max 30-day sparkline
  const last30d = aggregates.slice(0, 30).reverse();
  const vo2maxData = last30d
    .map(a => a.vo2_max ? parseFloat(a.vo2_max) : null)
    .filter((v): v is number => v !== null);

  return (
    <Tile
      icon={<BoltIcon style={{ width: 20, height: 20 }} />}
      title="Cardio Fitness"
      iconColor={COLORS.status.info}
      onClick={onClick}
    >
      {data.rhr_7d_avg !== null || data.vo2_max_latest !== null ? (
        <>
          {/* RHR */}
          {data.rhr_7d_avg !== null && (
            <div style={{ marginBottom: SPACING[12] }}>
              <div style={{ fontSize: TYPOGRAPHY.size['5xl'], fontWeight: 600, color: COLORS.text.primary }}>
                {Math.round(data.rhr_7d_avg)} bpm
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                RHR 7d avg
                {data.rhr_delta_30d !== null && (
                  <span style={{
                    color: data.rhr_delta_30d < 0 ? COLORS.status.success : COLORS.status.error,
                    marginLeft: SPACING[4],
                    fontWeight: 600
                  }}>
                    {data.rhr_delta_30d > 0 ? '+' : ''}{data.rhr_delta_30d.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* VO2 Max with sparkline */}
          {data.vo2_max_latest !== null && (
            <div
              style={{
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                padding: SPACING[8],
                cursor: onClickVO2 ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
              }}
              onClick={(e) => {
                if (onClickVO2) {
                  e.stopPropagation();
                  onClickVO2();
                }
              }}
              onMouseEnter={(e) => {
                if (onClickVO2) {
                  e.currentTarget.style.borderColor = COLORS.border.hover;
                  e.currentTarget.style.background = COLORS.bg.hover;
                }
              }}
              onMouseLeave={(e) => {
                if (onClickVO2) {
                  e.currentTarget.style.borderColor = COLORS.border.default;
                  e.currentTarget.style.background = COLORS.bg.base;
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>VO2 Max</div>
                  <div style={{ fontSize: TYPOGRAPHY.size['3xl'], fontWeight: 600, color: COLORS.text.primary, marginTop: SPACING[4] }}>
                    {data.vo2_max_latest.toFixed(1)}
                    {data.vo2_max_delta_90d !== null && (
                      <span style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        marginLeft: SPACING[4],
                        color: data.vo2_max_delta_90d > 0 ? COLORS.status.success : COLORS.status.error,
                        fontWeight: 600
                      }}>
                        {data.vo2_max_delta_90d > 0 ? '↗' : '↘'} {Math.abs(data.vo2_max_delta_90d).toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>

                {vo2maxData.length >= 2 && (
                  <div>
                    <Sparkline
                      data={vo2maxData}
                      width={50}
                      height={24}
                      color={data.vo2_max_delta_90d && data.vo2_max_delta_90d > 0 ? COLORS.status.success : COLORS.accent.default}
                    />
                    <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, textAlign: 'right', marginTop: SPACING[4] }}>
                      30d trend
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          message="No cardio data"
          action="Sync device"
        />
      )}
    </Tile>
  );
};

const TrainingLoadTile: React.FC<{ data: DashboardData['training_load']; aggregates: any[]; onOpenForm: () => void; onClick?: () => void; userAge?: number | null; stepsGoal?: number | null }> = ({ data, aggregates, onOpenForm, onClick, userAge, stepsGoal }) => {
  // Calculate max HR from age (220 - age) or default to 180
  const maxHR = userAge ? 220 - userAge : 180;
  // Workout type display names
  const workoutLabels: { [key: string]: string } = {
    running: 'Run',
    cycling: 'Bike',
    swimming: 'Swim',
    zone2: 'Z2',
    hiit: 'HIIT',
    strength: 'Str',
    intervals: 'Intervals',
    other: 'Other'
  };

  // Extract steps from aggregates (last 7 days) - use weekly average
  const last7d = aggregates.slice(0, 7);
  const stepsData = last7d.filter(a => a.steps && a.steps > 0);
  const avgDailySteps = stepsData.length > 0
    ? Math.round(stepsData.reduce((sum, a) => sum + (a.steps || 0), 0) / stepsData.length)
    : 0;

  // Training minutes (Z2+ only, excl walking)
  const walkingStats = data.workout_breakdown?.walking || { count: 0, total_minutes: 0 };
  const walkingMinutes = Math.round(walkingStats.total_minutes || 0);
  const trainingMinutes = data.total_minutes_week - walkingMinutes;

  // Goals - use user's configured step goal
  const STEPS_DAILY_GOAL = stepsGoal || 7000;
  const TRAINING_WEEKLY_GOAL = 150; // 150 min/week excl walking

  const stepsProgress = Math.min((avgDailySteps / STEPS_DAILY_GOAL) * 100, 100);
  const trainingProgress = Math.min((trainingMinutes / TRAINING_WEEKLY_GOAL) * 100, 100);

  return (
    <Tile
      icon={<ClockIcon style={{ width: 20, height: 20 }} />}
      title="Training Load"
      iconColor={COLORS.status.warning}
      onClick={onClick}
      action={
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenForm();
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: COLORS.text.secondary,
            padding: 0
          }}
        >
          <PlusIcon style={{ width: 16, height: 16 }} />
        </button>
      }
    >
      {data.total_minutes_week > 0 ? (
        <>
          {/* Steps vs Training Split */}
          <div style={{ display: 'flex', gap: SPACING[12], marginBottom: SPACING[12] }}>
            {/* Daily Steps */}
            <div style={{
              flex: 1,
              padding: SPACING[12],
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              borderLeft: '3px solid #10B981',
            }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
                Steps (7d avg)
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xl, fontWeight: 600, color: COLORS.text.primary }}>
                {avgDailySteps.toLocaleString()}
              </div>
              <div style={{
                marginTop: SPACING[4],
                height: 4,
                backgroundColor: COLORS.border.default,
                borderRadius: 2,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${stepsProgress}%`,
                  height: '100%',
                  backgroundColor: stepsProgress >= 100 ? COLORS.status.success : '#10B981',
                }} />
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                {Math.round(stepsProgress)}% of {STEPS_DAILY_GOAL.toLocaleString()}
              </div>
            </div>

            {/* Training (Z2+) */}
            <div style={{
              flex: 1,
              padding: SPACING[12],
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              borderLeft: '3px solid #EF4444',
            }}>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
                Training
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xl, fontWeight: 600, color: COLORS.text.primary }}>
                {trainingMinutes}m
              </div>
              <div style={{
                marginTop: SPACING[4],
                height: 4,
                backgroundColor: COLORS.border.default,
                borderRadius: 2,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${trainingProgress}%`,
                  height: '100%',
                  backgroundColor: trainingProgress >= 100 ? COLORS.status.success : '#EF4444',
                }} />
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                {Math.round(trainingProgress)}% of {TRAINING_WEEKLY_GOAL}m/wk
              </div>
            </div>
          </div>

          {/* Total summary line */}
          <div style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary,
            marginBottom: SPACING[12],
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              Total: <strong>{Math.floor(data.total_minutes_week / 60)}h {data.total_minutes_week % 60}m</strong>
            </span>
            {data.delta_vs_last_week !== null && (
              <span style={{
                color: Math.abs(data.delta_vs_last_week) > 20 ? COLORS.status.warning : COLORS.text.secondary,
                fontWeight: 600
              }}>
                {data.delta_vs_last_week > 0 ? '+' : ''}{data.delta_vs_last_week.toFixed(0)}% vs last week
              </span>
            )}
          </div>

          {/* Other workout types (Run, Strength, etc.) - compact */}
          {data.workout_breakdown && Object.keys(data.workout_breakdown).filter(t => t !== 'walking').length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: SPACING[4],
              marginBottom: SPACING[8]
            }}>
              {Object.entries(data.workout_breakdown)
                .filter(([type]) => type !== 'walking')
                .map(([type, stats]) => (
                <div
                  key={type}
                  style={{
                    background: COLORS.bg.base,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    padding: `${SPACING[4]}px ${SPACING[8]}px`,
                    fontSize: TYPOGRAPHY.size.xs,
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {workoutLabels[type] || type}: {stats.count}× ({Math.round(stats.total_minutes)}m)
                </div>
              ))}
            </div>
          )}

          {/* Hard & Strength metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            <div>
              <span style={{ color: COLORS.text.tertiary }}>Hard: </span>
              <span style={{ fontWeight: 600 }}>{data.hard_minutes_week}m</span>
            </div>
            <div>
              <span style={{ color: COLORS.text.tertiary }}>Strength: </span>
              <span style={{ fontWeight: 600 }}>{data.strength_sessions_week}×</span>
            </div>
          </div>

          {/* HR Zone Breakdown - Separate bars */}
          {data.hr_zones && (data.hr_zones.zone_2 > 0 || data.hr_zones.zone_3 > 0 || data.hr_zones.zone_4 > 0 || data.hr_zones.zone_5 > 0) && (
            <div style={{ marginTop: SPACING[12] }}>
              <div style={{
                fontSize: TYPOGRAPHY.size['2xs'],
                color: COLORS.text.tertiary,
                marginBottom: SPACING[8],
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>HR ZONES:</span>
                {data.total_trimp && data.total_trimp > 0 && (
                  <span style={{ fontWeight: 400 }}>TRIMP: {data.total_trimp}</span>
                )}
              </div>
              {/* Separate bar for each zone */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                {(() => {
                  // Z2+ only - Z1 (walking) tracked separately via steps
                  const zones = [
                    { zone: 'Z2', label: 'Aerobic', value: data.hr_zones.zone_2, color: '#50C878', pctMin: 60, pctMax: 70 },
                    { zone: 'Z3', label: 'Tempo', value: data.hr_zones.zone_3, color: '#F5B041', pctMin: 70, pctMax: 80 },
                    { zone: 'Z4', label: 'Threshold', value: data.hr_zones.zone_4, color: '#E67E22', pctMin: 80, pctMax: 90 },
                    { zone: 'Z5', label: 'Max', value: data.hr_zones.zone_5, color: '#E74C3C', pctMin: 90, pctMax: 100 },
                  ];
                  const maxValue = Math.max(...zones.map(z => z.value), 1);
                  return zones.map(({ zone, label, value, color, pctMin, pctMax }) => {
                    const hrMin = Math.round(maxHR * pctMin / 100);
                    const hrMax = Math.round(maxHR * pctMax / 100);
                    return (
                      <div key={zone}>
                        {/* Zone label row */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '2px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                            <span style={{
                              fontSize: TYPOGRAPHY.size['2xs'],
                              fontWeight: 600,
                              color: color,
                            }}>
                              {zone}
                            </span>
                            <span style={{
                              fontSize: TYPOGRAPHY.size['2xs'],
                              color: COLORS.text.tertiary,
                            }}>
                              {label}
                            </span>
                          </div>
                          <span style={{
                            fontSize: TYPOGRAPHY.size['2xs'],
                            color: COLORS.text.secondary,
                          }}>
                            {pctMin}-{pctMax}% · {hrMin}-{hrMax}
                          </span>
                        </div>
                        {/* Bar row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                          <div style={{
                            flex: 1,
                            height: '12px',
                            backgroundColor: COLORS.bg.base,
                            borderRadius: RADIUS,
                            overflow: 'hidden',
                            position: 'relative',
                          }}>
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              height: '100%',
                              width: `${(value / maxValue) * 100}%`,
                              backgroundColor: color,
                              opacity: 0.8,
                              transition: 'width 0.3s ease',
                            }} />
                          </div>
                          <span style={{
                            fontSize: TYPOGRAPHY.size['2xs'],
                            fontWeight: 600,
                            color: value > 0 ? COLORS.text.primary : COLORS.text.tertiary,
                            minWidth: '32px',
                            textAlign: 'right',
                          }}>
                            {value > 0 ? `${value}m` : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          message="No training this week"
          action="Log workout"
          onAction={onOpenForm}
        />
      )}
    </Tile>
  );
};

interface HevyWorkout {
  id: number;
  date: string;
  workout_type: string;
  duration_minutes: number | null;
  notes: string;
  source: string;
}

interface HevyStrengthSet {
  id: number;
  date: string;
  exercise: string;
  exercise_display: string;
  exercise_custom: string;
  weight_kg: string;
  reps: number;
  sets: number;
  duration_seconds: number | null;
  e1rm: string | null;
  source: string;
}

interface HevyRoutineData {
  id: number;
  title: string;
  exercises: Array<{
    index: number;
    title: string;
    notes: string;
    sets: Array<{ reps: number | null; weight_kg: number | null; duration_seconds: number | null }>;
    rest_seconds: number;
  }>;
}

const StrengthTile: React.FC<{ data: DashboardData['strength']; onOpenForm: () => void; onClick?: () => void }> = ({ data, onOpenForm, onClick }) => {
  const [recentWorkouts, setRecentWorkouts] = useState<HevyWorkout[]>([]);
  const [recentSets, setRecentSets] = useState<HevyStrengthSet[]>([]);
  const [routines, setRoutines] = useState<HevyRoutineData[]>([]);
  const [loadingHevy, setLoadingHevy] = useState(false);

  useEffect(() => {
    const fetchHevyData = async () => {
      try {
        setLoadingHevy(true);
        const [wsRes, ssRes, routineRes] = await Promise.all([
          apiClient.get('/api/workout-sessions/', { params: { source: 'hevy', ordering: '-date', page_size: 5 } }),
          apiClient.get('/api/strength-sessions/', { params: { source: 'hevy', ordering: '-date', page_size: 20 } }),
          apiClient.get('/api/hevy-routines/'),
        ]);
        setRecentWorkouts(wsRes.data.results ?? wsRes.data);
        setRecentSets(ssRes.data.results ?? ssRes.data);
        setRoutines(routineRes.data.results ?? routineRes.data);
      } catch (err) {
        // Silently fail — Hevy section just won't show
      } finally {
        setLoadingHevy(false);
      }
    };
    fetchHevyData();
  }, []);

  // Exercise display names
  const exerciseLabels: { [key: string]: string } = {
    squat: 'Squat',
    bench_press: 'Bench Press',
    deadlift: 'Deadlift',
    overhead_press: 'Overhead Press',
    barbell_row: 'Barbell Row',
    pull_up: 'Pull-up',
    dumbbell_press: 'Dumbbell Press',
    leg_press: 'Leg Press',
    romanian_deadlift: 'Romanian DL',
    lat_pulldown: 'Lat Pulldown'
  };

  // Group strength sets by workout date
  const setsByDate: { [date: string]: HevyStrengthSet[] } = {};
  recentSets.forEach(s => {
    if (!setsByDate[s.date]) setsByDate[s.date] = [];
    setsByDate[s.date].push(s);
  });

  return (
    <Tile
      icon={<FireIcon style={{ width: 20, height: 20 }} />}
      title="Strength Level"
      iconColor={COLORS.status.error}
      onClick={onClick}
      action={
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenForm();
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: COLORS.text.secondary,
            padding: 0
          }}
        >
          <PlusIcon style={{ width: 16, height: 16 }} />
        </button>
      }
    >
      {data.top_lift_e1rm !== null && data.top_lift_exercise ? (
        <>
          {/* Top lift */}
          <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
            TOP LIFT (4 weeks)
          </div>

          <div style={{
            background: COLORS.bg.base,
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            padding: SPACING[12]
          }}>
            <div style={{ fontSize: TYPOGRAPHY.size.md, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
              {exerciseLabels[data.top_lift_exercise] || data.top_lift_exercise}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING[8] }}>
              <div style={{ fontSize: TYPOGRAPHY.size['6xl'], fontWeight: 600, color: COLORS.text.primary }}>
                {data.top_lift_e1rm.toFixed(0)}kg
              </div>

              {data.delta_4week_pct !== null && (
                <div style={{
                  fontSize: TYPOGRAPHY.size.lg,
                  fontWeight: 600,
                  color: data.delta_4week_pct > 0 ? COLORS.status.success :
                         data.delta_4week_pct < 0 ? COLORS.status.error :
                         COLORS.text.secondary
                }}>
                  {data.delta_4week_pct > 0 ? '↗' : data.delta_4week_pct < 0 ? '↘' : '→'}
                  {' '}
                  {Math.abs(data.delta_4week_pct).toFixed(1)}%
                </div>
              )}
            </div>

            <div style={{
              fontSize: TYPOGRAPHY.size['2xs'],
              color: COLORS.text.tertiary,
              marginTop: SPACING[8],
              fontStyle: 'italic'
            }}>
              e1RM (4-week trend)
            </div>
          </div>
        </>
      ) : routines.length > 0 ? (
        <div>
          <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginBottom: SPACING[8] }}>
            NO WORKOUTS YET
          </div>
          <div style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginBottom: SPACING[12] }}>
            Complete a routine in Hevy to start tracking
          </div>
        </div>
      ) : (
        <EmptyState
          message="No strength data"
          action="Log lift"
          onAction={onOpenForm}
        />
      )}

      {/* Hevy Routines (always show when available) */}
      {!loadingHevy && routines.length > 0 && recentWorkouts.length === 0 && (
        <div style={{ marginTop: data.top_lift_e1rm !== null ? SPACING[16] : 0 }}>
          <div style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: COLORS.text.tertiary,
            marginBottom: SPACING[8],
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Routines (Hevy)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
            {routines.map((routine) => (
              <div key={routine.id} style={{
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                padding: SPACING[8],
              }}>
                <div style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: 600, color: COLORS.text.primary, marginBottom: SPACING[4] }}>
                  {routine.title}
                </div>
                {routine.exercises.map((ex, i) => {
                  const setsCount = ex.sets.length;
                  const reps = ex.sets[0]?.reps;
                  const duration = ex.sets[0]?.duration_seconds;
                  const detail = reps ? `${setsCount}×${reps}` : duration ? `${setsCount}×${duration}s` : `${setsCount} sets`;
                  return (
                    <div key={i} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.secondary,
                      paddingTop: 2,
                    }}>
                      <span>{ex.title}</span>
                      <span style={{ color: COLORS.text.tertiary }}>{detail}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Hevy Workouts */}
      {!loadingHevy && recentWorkouts.length > 0 && (
        <div style={{ marginTop: SPACING[16] }}>
          <div style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: COLORS.text.tertiary,
            marginBottom: SPACING[8],
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Recent Workouts (Hevy)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
            {recentWorkouts.map((w) => {
              const workoutSets = setsByDate[w.date] || [];
              // Group sets by exercise name
              const byExercise: { [name: string]: HevyStrengthSet[] } = {};
              workoutSets.forEach(s => {
                const name = s.exercise === 'other' ? s.exercise_custom : (exerciseLabels[s.exercise] || s.exercise_display);
                if (!byExercise[name]) byExercise[name] = [];
                byExercise[name].push(s);
              });

              return (
                <div key={w.id} style={{
                  background: COLORS.bg.base,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  padding: SPACING[8],
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: workoutSets.length > 0 ? SPACING[4] : 0,
                  }}>
                    <span style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: 600, color: COLORS.text.primary }}>
                      {new Date(w.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    {w.duration_minutes && (
                      <span style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
                        {w.duration_minutes}min
                      </span>
                    )}
                  </div>
                  {Object.entries(byExercise).map(([name, sets]) => {
                    // For timed exercises (plank, etc.), find the longest duration
                    const hasDuration = sets.some(s => s.duration_seconds && s.duration_seconds > 0);
                    let detail = '';
                    if (hasDuration) {
                      const totalDuration = sets.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
                      const bestDuration = Math.max(...sets.map(s => s.duration_seconds || 0));
                      detail = `${sets.length}x — best ${bestDuration}s (total ${totalDuration}s)`;
                    } else {
                      const topSet = sets.reduce((best, s) =>
                        parseFloat(s.weight_kg) > parseFloat(best.weight_kg) ? s : best
                      , sets[0]);
                      detail = `${sets.length}x — top ${topSet.weight_kg}kg × ${topSet.reps}`;
                    }
                    return (
                      <div key={name} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: TYPOGRAPHY.size.xs,
                        color: COLORS.text.secondary,
                        paddingTop: 2,
                      }}>
                        <span>{name}</span>
                        <span style={{ color: COLORS.text.tertiary }}>
                          {detail}
                        </span>
                      </div>
                    );
                  })}
                  {workoutSets.length === 0 && w.notes && (
                    <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary }}>
                      {w.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Tile>
  );
};

const BodyCompTile: React.FC<{
  data: DashboardData['body_comp'];
  onOpenForm: () => void;
  onClick?: () => void;
  weightGoal?: { target_value: number; start_value: number | null; start_date: string; end_date: string } | null;
  aggregates?: any[];
}> = ({ data, onOpenForm, onClick, weightGoal, aggregates }) => {
  // Get latest weight from aggregates (matches what graph shows)
  const getLatestWeight = (): number | null => {
    if (!aggregates || aggregates.length === 0) return null;
    // Find most recent entry with weight data
    for (const agg of aggregates) {
      if (agg.body_weight_kg !== null && agg.body_weight_kg !== undefined) {
        const weight = Number(agg.body_weight_kg);
        return isNaN(weight) ? null : weight;
      }
    }
    return null;
  };

  // Calculate expected weight based on goal progress
  const getExpectedWeight = () => {
    if (!weightGoal || weightGoal.start_value === null) return null;
    const startDate = new Date(weightGoal.start_date);
    const endDate = new Date(weightGoal.end_date);
    const today = new Date();
    const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    const daysElapsed = (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysElapsed < 0) return weightGoal.start_value; // Before start
    if (daysElapsed > totalDays) return weightGoal.target_value; // After end
    const progress = daysElapsed / totalDays;
    return weightGoal.start_value + (weightGoal.target_value - weightGoal.start_value) * progress;
  };
  const expectedWeight = getExpectedWeight();
  // Use latest weight if available, fall back to 7d avg
  const latestWeight = getLatestWeight();
  const currentWeight = latestWeight ?? data.weight_7d_avg;
  const weightDiff = currentWeight !== null && expectedWeight !== null ? currentWeight - expectedWeight : null;
  // Round to 1 decimal for display; treat < 0.1kg difference as "on target"
  const roundedDiff = weightDiff !== null ? Math.round(weightDiff * 10) / 10 : null;
  return (
    <Tile
      icon={<ScaleIcon style={{ width: 20, height: 20 }} />}
      title="Body Composition"
      iconColor={COLORS.text.secondary}
      onClick={onClick}
      action={
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenForm();
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: COLORS.text.secondary,
            padding: 0
          }}
        >
          <PlusIcon style={{ width: 16, height: 16 }} />
        </button>
      }
    >
      {data.weight_7d_avg !== null || data.waist_latest !== null ? (
        <>
          {/* Weight */}
          {data.weight_7d_avg !== null && (
            <div style={{ marginBottom: SPACING[12] }}>
              <div style={{ fontSize: TYPOGRAPHY.size['6xl'], fontWeight: 600, color: COLORS.text.primary }}>
                {data.weight_7d_avg.toFixed(1)}kg
              </div>
              <div style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[4] }}>
                weight 7d avg
                {data.weight_delta_30d !== null && (
                  <span style={{
                    color: Math.abs(data.weight_delta_30d) > 2 ? COLORS.status.warning : COLORS.text.secondary,
                    marginLeft: SPACING[4],
                    fontWeight: 600
                  }}>
                    {data.weight_delta_30d > 0 ? '+' : ''}{data.weight_delta_30d.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Secondary metrics - 2-COLUMN COMPACT */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            {data.body_fat_trend !== null && (
              <div>
                <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.size['2xs'] }}>Body Fat</div>
                <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.size.lg, marginTop: SPACING[4] }}>
                  {data.body_fat_trend.toFixed(1)}%
                </div>
              </div>
            )}

            {data.bmi !== null && (
              <div>
                <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.size['2xs'] }}>BMI</div>
                <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.size.lg, marginTop: SPACING[4] }}>
                  {data.bmi.toFixed(1)}
                </div>
              </div>
            )}

            {data.waist_latest !== null && (
              <div>
                <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.size['2xs'] }}>Waist</div>
                <div style={{ fontWeight: 600, fontSize: TYPOGRAPHY.size.lg, marginTop: SPACING[4] }}>
                  {data.waist_latest.toFixed(1)}cm
                </div>
              </div>
            )}
          </div>

          {/* Weight Goal */}
          {weightGoal && (
            <div style={{
              marginTop: SPACING[12],
              padding: SPACING[8],
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              borderLeft: `3px solid ${roundedDiff !== null && roundedDiff > 0 ? COLORS.status.warning : COLORS.status.success}`
            }}>
              <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary }}>Goal</div>
              <div style={{ fontSize: TYPOGRAPHY.size.md, fontWeight: 600, color: COLORS.status.success, marginTop: SPACING[4] }}>
                {currentWeight !== null && typeof currentWeight === 'number' && `${currentWeight.toFixed(1)} → `}{weightGoal.target_value.toFixed(1)}kg
                <span style={{ fontSize: TYPOGRAPHY.size.xs, fontWeight: 400, color: COLORS.text.tertiary, marginLeft: SPACING[8] }}>
                  by {new Date(weightGoal.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              {roundedDiff !== null && expectedWeight !== null && (
                <div style={{ fontSize: TYPOGRAPHY.size.xs, marginTop: SPACING[4], color: roundedDiff > 0 ? COLORS.status.warning : COLORS.status.success }}>
                  {roundedDiff > 0 ? (
                    <>{`+${roundedDiff.toFixed(1)}kg above target`} <span style={{ color: COLORS.text.tertiary }}>(expected {expectedWeight.toFixed(1)}kg)</span></>
                  ) : roundedDiff < 0 ? (
                    <>{`${roundedDiff.toFixed(1)}kg ahead of target`} <span style={{ color: COLORS.text.tertiary }}>(expected {expectedWeight.toFixed(1)}kg)</span></>
                  ) : (
                    <>On target <span style={{ color: COLORS.text.tertiary }}>(expected {expectedWeight.toFixed(1)}kg)</span></>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState
          message="No body comp data"
          action="Log weight"
          onAction={onOpenForm}
        />
      )}
    </Tile>
  );
};

// Physiology Tile - Training State & Readiness
const PhysiologyTile: React.FC<{ data: PhysiologyData | null }> = ({ data }) => {
  if (!data) {
    return (
      <Tile
        icon={<ChartBarIcon style={{ width: 20, height: 20 }} />}
        title="Training State"
        iconColor={COLORS.status.info}
      >
        <div style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.size.md, textAlign: 'center', padding: SPACING[16] }}>
          No physiology data available
        </div>
      </Tile>
    );
  }

  const stateColors: { [key: string]: string } = {
    push: COLORS.status.success,
    maintain: COLORS.status.info,
    deload: COLORS.status.warning,
    blocked: COLORS.status.error
  };

  const stateLabels: { [key: string]: string } = {
    push: 'PUSH',
    maintain: 'MAINTAIN',
    deload: 'DELOAD',
    blocked: 'BLOCKED'
  };

  const stateDescriptions: { [key: string]: string } = {
    push: 'Ready to progress - load is balanced',
    maintain: 'Steady state - keep current volume',
    deload: 'Reduce volume 50% - recovery needed',
    blocked: 'Rest day required - recovery critical'
  };

  const state = data.training_state.state;
  const readiness = data.readiness.score;
  const acwr = data.loads.acwr;
  const deloadUntil = data.deload_until;

  // ACWR status
  const getAcwrStatus = (value: number | null) => {
    if (value === null) return { color: COLORS.text.tertiary, label: 'N/A' };
    if (value < 0.8) return { color: COLORS.status.info, label: 'Under-trained' };
    if (value <= 1.05) return { color: COLORS.status.success, label: 'Optimal' };
    if (value <= 1.15) return { color: COLORS.status.warning, label: 'Caution' };
    return { color: COLORS.status.error, label: 'High Risk' };
  };

  const acwrStatus = getAcwrStatus(acwr);

  return (
    <Tile
      icon={<ChartBarIcon style={{ width: 20, height: 20 }} />}
      title="Training State"
      iconColor={stateColors[state]}
    >
      {/* Training State Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING[12],
        marginBottom: SPACING[16]
      }}>
        <div style={{
          padding: `${SPACING[8]}px ${SPACING[16]}px`,
          background: `${stateColors[state]}22`,
          border: `2px solid ${stateColors[state]}`,
          borderRadius: RADIUS,
          fontSize: TYPOGRAPHY.size.xl,
          fontWeight: 700,
          color: stateColors[state],
          letterSpacing: '1px'
        }}>
          {stateLabels[state]}
        </div>
      </div>

      {/* State Description */}
      <div style={{
        fontSize: TYPOGRAPHY.size.sm,
        color: COLORS.text.secondary,
        marginBottom: SPACING[16],
        lineHeight: 1.4
      }}>
        {stateDescriptions[state]}
      </div>

      {/* Deload Notice */}
      {deloadUntil && (
        <div style={{
          background: `${COLORS.status.warning}11`,
          border: `1px solid ${COLORS.status.warning}33`,
          borderRadius: RADIUS,
          padding: SPACING[8],
          marginBottom: SPACING[12],
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.status.warning
        }}>
          Deload until: {new Date(deloadUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
      )}

      {/* Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: SPACING[12],
        marginTop: 'auto'
      }}>
        {/* Readiness */}
        <div style={{
          background: COLORS.bg.base,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          padding: SPACING[8],
          textAlign: 'center'
        }}>
          <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
            READINESS
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.size['3xl'],
            fontWeight: 600,
            color: readiness >= 70 ? COLORS.status.success :
                   readiness >= 50 ? COLORS.status.warning :
                   COLORS.status.error
          }}>
            {readiness}
          </div>
        </div>

        {/* ACWR */}
        <div style={{
          background: COLORS.bg.base,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          padding: SPACING[8],
          textAlign: 'center'
        }}>
          <div style={{ fontSize: TYPOGRAPHY.size['2xs'], color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
            ACWR
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.size['3xl'],
            fontWeight: 600,
            color: acwrStatus.color
          }}>
            {acwr !== null ? acwr.toFixed(2) : '-'}
          </div>
          <div style={{ fontSize: TYPOGRAPHY.size['3xs'], color: acwrStatus.color, marginTop: SPACING[4] }}>
            {acwrStatus.label}
          </div>
        </div>
      </div>

      {/* Load Numbers */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: SPACING[12],
        fontSize: TYPOGRAPHY.size.xs,
        color: COLORS.text.tertiary
      }}>
        <span>Acute 7d: {data.loads.acute_7d.toFixed(0)}</span>
        <span>Chronic 28d: {data.loads.chronic_28d_weekly.toFixed(0)}</span>
      </div>
    </Tile>
  );
};

// Tile Wrapper Component

interface TileProps {
  icon: React.ReactNode;
  title: string;
  iconColor: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  onClick?: () => void;
}

const Tile: React.FC<TileProps> = ({ icon, title, iconColor, children, action, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: COLORS.bg.panel,
        border: `1px solid ${isHovered && onClick ? COLORS.border.hover : COLORS.border.default}`,
        borderRadius: RADIUS,
        padding: SPACING[16],
        minHeight: '160px',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transform: isHovered && onClick ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.2s ease'
      }}
      onClick={onClick}
      onMouseEnter={() => onClick && setIsHovered(true)}
      onMouseLeave={() => onClick && setIsHovered(false)}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: SPACING[12],
        paddingBottom: SPACING[8],
        borderBottom: `1px solid ${COLORS.border.default}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <div style={{ color: iconColor }}>
            {icon}
          </div>
          <h3 style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: 600,
            color: COLORS.text.secondary,
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {title}
          </h3>
        </div>
        {action}
      </div>
      <div style={{ flex: 1 }}>
        {children}
      </div>
    </div>
  );
};

// Empty State Component

interface EmptyStateProps {
  message: string;
  action: string;
  onAction?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ message, action, onAction }) => {
  return (
    <div style={{
      textAlign: 'center',
      padding: `${SPACING[32]} 0`,
      color: COLORS.text.secondary
    }}>
      <div style={{ marginBottom: SPACING[16], fontSize: TYPOGRAPHY.size.sm }}>
        {message}
      </div>
      {onAction ? (
        <button
          onClick={onAction}
          style={{
            padding: `${SPACING[12]} ${SPACING[16]}`,
            background: COLORS.accent.default,
            color: COLORS.text.primary,
            border: 'none',
            borderRadius: RADIUS,
            cursor: 'pointer',
            fontSize: TYPOGRAPHY.size.xs,
            fontWeight: 500
          }}
        >
          {action}
        </button>
      ) : (
        <div style={{ fontSize: TYPOGRAPHY.size.xs, fontStyle: 'italic' }}>
          {action}
        </div>
      )}
    </div>
  );
};

export default CoachDashboard;
